/**
 * 통합 국가/도시 데이터
 * - 국가 및 도시 기본 정보
 * - 다국어 번역 (한국어, 일본어, 중국어 등)
 * - 좌표 정보 (위도, 경도)
 */

import { placeSlug } from "@/lib/search/slugify";
import type { Locale } from "@/i18n/routing";
import { DESTINATION_CITIES, EXISTING_CITY_MEMBERSHIP } from "./destinations.generated";

/** 장소 이름 — en·ko 필수, 나머지 locale 은 선택(호출부가 en/ko 로 폴백). */
export type LocalizedPlaceName = { en: string; ko: string } & Partial<Record<Locale, string>>;

export interface Country {
  code: string; // ISO country code
  name: LocalizedPlaceName;
}

export interface City {
  id: string;
  countryCode: string;
  // th·vi 는 앱 locale 이 아니어도 도시 데이터에 미리 들어 있을 수 있어 남겨 둔다.
  name: LocalizedPlaceName & { th?: string; vi?: string };
  coordinates?: {
    latitude: number;
    longitude: number;
  };
  radius_km?: number; // 도시 검색 박스 반폭 + 메인 페이지 도시 섹션 노출 반경. 미설정 시 DEFAULT_CITY_RADIUS_KM. 지점형(공항 등)은 좁히고 광역(오사카부·대마도 섬 등)은 늘림.
  mapZoom?: number; // 검색 지도 고정 줌(명시값). 지정 시 상품 마커 자동맞춤을 끄고 이 줌으로 도시 중심 표시. (공항 등 지점형)
  imageUrl?: string;
  priority?: number; // 인기순 정렬용 (낮을수록 상위, 미설정 시 999)
  /**
   * 행정구역 소속(10-19) — 지점의 geo 코드(branch_info.geo_admin1/2_code)로 도시에 넣는다.
   * 판정 한 곳: lib/seo/product-backed-cities.branchInCity. 없으면 반경만.
   */
  membership?: CityMembership;
}

/**
 * 도시 소속 규칙. 코드 목록에 든 지점 ∪ (radius !== false 면) 중심 반경 안 지점.
 * 일본은 시정촌(admin2) 목록, 한국 광역시는 시도(admin1) 전체. 지점에 코드가 없으면(geo 판정
 * 실패) 반경으로 판정한다.
 */
export interface CityMembership {
  /** geo_admin2.code("JP.13101") — 이 시군구의 지점 */
  admin2?: string[];
  /** geo_admin1.code("KR-11") — 이 시도 전체의 지점 */
  admin1?: string[];
  /** admin1 안에서 뺄 시군구(인천 중구·옹진 = 인천공항 랜딩 몫) */
  excludeAdmin2?: string[];
  /** false 면 반경 원을 쓰지 않는다(새 여행지 — 이웃 도시 침범 방지). 기본 true */
  radius?: boolean;
}

/**
 * 일반 도시 공통 기본 반경(km). radius_km 미설정 도시에 적용되는 단일 기본값.
 * 도시 검색 박스 생성(getCityBounds·getCityBoundsOffset)과 홈 도시 섹션 노출(HomeServerSections)이
 * 모두 이 값을 폴백으로 사용 → 진입 경로별 불일치 제거. 특수 도시는 각자 radius_km로 오버라이드.
 * 위도 1도 ≈ 111km 이므로 도 단위 offset = DEFAULT_CITY_RADIUS_KM / 111.
 */
export const DEFAULT_CITY_RADIUS_KM = 12;

export const COUNTRIES: Country[] = [
  // 아시아
  {
    code: "KR",
    name: {
      en: "South Korea",
      ko: "대한민국",
      ja: "韓国",
      "zh-CN": "韩国",
      "zh-TW": "韓國",
    },
  },
  {
    code: "JP",
    name: {
      en: "Japan",
      ko: "일본",
      ja: "日本",
      "zh-CN": "日本",
      "zh-TW": "日本",
    },
  },
  {
    code: "CN",
    name: {
      en: "China",
      ko: "중국",
      ja: "中国",
      "zh-CN": "中国",
      "zh-TW": "中國",
    },
  },
  {
    code: "TW",
    name: {
      en: "Taiwan",
      ko: "대만",
      ja: "台湾",
      "zh-CN": "台湾",
      "zh-TW": "台灣",
    },
  },
  {
    code: "HK",
    name: {
      en: "Hong Kong",
      ko: "홍콩",
      ja: "香港",
      "zh-CN": "香港",
      "zh-TW": "香港",
    },
  },
  // 동남아시아
  {
    code: "TH",
    name: {
      en: "Thailand",
      ko: "태국",
      ja: "タイ",
      "zh-CN": "泰国",
      "zh-TW": "泰國",
    },
  },
  {
    code: "VN",
    name: {
      en: "Vietnam",
      ko: "베트남",
      ja: "ベトナム",
      "zh-CN": "越南",
      "zh-TW": "越南",
    },
  },
  {
    code: "SG",
    name: {
      en: "Singapore",
      ko: "싱가포르",
      ja: "シンガポール",
      "zh-CN": "新加坡",
      "zh-TW": "新加坡",
    },
  },
  {
    code: "MY",
    name: {
      en: "Malaysia",
      ko: "말레이시아",
      ja: "マレーシア",
      "zh-CN": "马来西亚",
      "zh-TW": "馬來西亞",
    },
  },
  {
    code: "ID",
    name: {
      en: "Indonesia",
      ko: "인도네시아",
      ja: "インドネシア",
      "zh-CN": "印度尼西亚",
      "zh-TW": "印度尼西亞",
    },
  },
  {
    code: "PH",
    name: {
      en: "Philippines",
      ko: "필리핀",
      ja: "フィリピン",
      "zh-CN": "菲律宾",
      "zh-TW": "菲律賓",
    },
  },
  // 기타 아시아
  {
    code: "IN",
    name: {
      en: "India",
      ko: "인도",
      ja: "インド",
      "zh-CN": "印度",
      "zh-TW": "印度",
    },
  },
  {
    code: "AE",
    name: {
      en: "United Arab Emirates",
      ko: "아랍에미리트",
      ja: "アラブ首長国連邦",
      "zh-CN": "阿联酋",
      "zh-TW": "阿聯酋",
    },
  },
  // 오세아니아
  {
    code: "AU",
    name: {
      en: "Australia",
      ko: "호주",
      ja: "オーストラリア",
      "zh-CN": "澳大利亚",
      "zh-TW": "澳大利亞",
    },
  },
  {
    code: "NZ",
    name: {
      en: "New Zealand",
      ko: "뉴질랜드",
      ja: "ニュージーランド",
      "zh-CN": "新西兰",
      "zh-TW": "紐西蘭",
    },
  },
  // 북미
  {
    code: "US",
    name: {
      en: "United States",
      ko: "미국",
      ja: "アメリカ",
      "zh-CN": "美国",
      "zh-TW": "美國",
    },
  },
  // 유럽
  {
    code: "GB",
    name: {
      en: "United Kingdom",
      ko: "영국",
      ja: "イギリス",
      "zh-CN": "英国",
      "zh-TW": "英國",
    },
  },
  {
    code: "FR",
    name: {
      en: "France",
      ko: "프랑스",
      ja: "フランス",
      "zh-CN": "法国",
      "zh-TW": "法國",
    },
  },
  {
    code: "DE",
    name: {
      en: "Germany",
      ko: "독일",
      ja: "ドイツ",
      "zh-CN": "德国",
      "zh-TW": "德國",
    },
  },
  {
    code: "IT",
    name: {
      en: "Italy",
      ko: "이탈리아",
      ja: "イタリア",
      "zh-CN": "意大利",
      "zh-TW": "義大利",
    },
  },
  {
    code: "ES",
    name: {
      en: "Spain",
      ko: "스페인",
      ja: "スペイン",
      "zh-CN": "西班牙",
      "zh-TW": "西班牙",
    },
  },
];

const BASE_CITIES: City[] = [
  // ===== 한국 =====
  {
    id: "KR-SEL",
    countryCode: "KR",
    name: {
      en: "Seoul",
      ko: "서울",
      ja: "ソウル",
      "zh-CN": "首尔",
      "zh-TW": "首爾",
    },
    coordinates: { latitude: 37.5665, longitude: 126.978 },
    priority: 1, // 가장 인기
  },
  {
    id: "KR-PUS",
    countryCode: "KR",
    name: {
      en: "Busan",
      ko: "부산",
      ja: "釜山",
      "zh-CN": "釜山",
      "zh-TW": "釜山",
    },
    coordinates: { latitude: 35.1796, longitude: 129.0756 },
    priority: 2,
  },
  {
    id: "KR-ICN",
    countryCode: "KR",
    name: {
      en: "Incheon",
      ko: "인천",
      ja: "仁川",
      "zh-CN": "仁川",
      "zh-TW": "仁川",
    },
    coordinates: { latitude: 37.4563, longitude: 126.7052 },
    priority: 4,
  },
  {
    id: "KR-ICA",
    countryCode: "KR",
    name: {
      en: "Incheon Airport",
      ko: "인천공항",
      ja: "仁川空港",
      "zh-CN": "仁川机场",
      "zh-TW": "仁川機場",
    },
    coordinates: { latitude: 37.4602, longitude: 126.4407 },
    radius_km: 3, // 공항 핵심부만 노출 — 인스파이어 아레나(서쪽 4.5km) 제외. 검색 박스 ±3km.
    mapZoom: 13, // 공항 전체+주변이 보이는 고정 줌 (상품 위치와 무관하게 공항 중심 표시)
    priority: 4,
  },
  {
    id: "KR-DGU",
    countryCode: "KR",
    name: {
      en: "Daegu",
      ko: "대구",
      ja: "大邱",
      "zh-CN": "大邱",
      "zh-TW": "大邱",
    },
    coordinates: { latitude: 35.8714, longitude: 128.6014 },
    priority: 5,
  },
  {
    id: "KR-DJN",
    countryCode: "KR",
    name: {
      en: "Daejeon",
      ko: "대전",
      ja: "大田",
      "zh-CN": "大田",
      "zh-TW": "大田",
    },
    coordinates: { latitude: 36.3504, longitude: 127.3845 },
    priority: 7,
  },
  {
    id: "KR-GWJ",
    countryCode: "KR",
    name: {
      en: "Gwangju",
      ko: "광주",
      ja: "光州",
      "zh-CN": "光州",
      "zh-TW": "光州",
    },
    coordinates: { latitude: 35.1595, longitude: 126.8526 },
    priority: 6,
  },
  {
    id: "KR-USN",
    countryCode: "KR",
    name: {
      en: "Ulsan",
      ko: "울산",
      ja: "蔚山",
      "zh-CN": "蔚山",
      "zh-TW": "蔚山",
    },
    coordinates: { latitude: 35.5384, longitude: 129.3114 },
    priority: 8,
  },
  {
    id: "KR-SWN",
    countryCode: "KR",
    name: {
      en: "Suwon",
      ko: "수원",
      ja: "水原",
      "zh-CN": "水原",
      "zh-TW": "水原",
    },
    coordinates: { latitude: 37.2636, longitude: 127.0286 },
    priority: 9,
  },
  {
    id: "KR-JEJ",
    countryCode: "KR",
    name: {
      en: "Jeju",
      ko: "제주",
      ja: "済州",
      "zh-CN": "济州",
      "zh-TW": "濟州",
    },
    coordinates: { latitude: 33.4996, longitude: 126.5312 },
    priority: 3, // 제주 인기 높음
  },
  {
    id: "KR-JJN",
    countryCode: "KR",
    name: {
      en: "Jeonju",
      ko: "전주",
      ja: "全州",
      "zh-CN": "全州",
      "zh-TW": "全州",
    },
    coordinates: { latitude: 35.8242, longitude: 127.148 },
    priority: 11,
  },
  {
    id: "KR-GYG",
    countryCode: "KR",
    name: {
      en: "Goyang",
      ko: "고양",
      ja: "高陽",
      "zh-CN": "高阳",
      "zh-TW": "高陽",
    },
    coordinates: { latitude: 37.6584, longitude: 126.832 },
    priority: 10,
  },

  // ===== 일본 =====
  {
    id: "JP-TYO",
    countryCode: "JP",
    name: {
      en: "Tokyo",
      ko: "도쿄",
      ja: "東京",
      "zh-CN": "东京",
      "zh-TW": "東京",
    },
    coordinates: { latitude: 35.6812, longitude: 139.7671 },
    priority: 1, // 일본 내 1위
  },
  {
    id: "JP-OSA",
    countryCode: "JP",
    name: {
      en: "Osaka",
      ko: "오사카",
      ja: "大阪",
      "zh-CN": "大阪",
      "zh-TW": "大阪",
    },
    coordinates: { latitude: 34.6937, longitude: 135.5023 },
    radius_km: 20, // 사카이·셋쓰·가도마 등 오사카부 광역권을 포괄(중심 ±20km)
    priority: 2, // 일본 내 2위
  },
  {
    id: "JP-KYO",
    countryCode: "JP",
    name: {
      en: "Kyoto",
      ko: "교토",
      ja: "京都",
      "zh-CN": "京都",
      "zh-TW": "京都",
    },
    coordinates: { latitude: 35.0116, longitude: 135.7681 },
    priority: 4, // 일본 내 4위
  },
  {
    id: "JP-FUK",
    countryCode: "JP",
    name: {
      en: "Fukuoka",
      ko: "후쿠오카",
      ja: "福岡",
      "zh-CN": "福冈",
      "zh-TW": "福岡",
    },
    coordinates: { latitude: 33.5904, longitude: 130.4017 },
    priority: 3, // 일본 내 3위
  },
  {
    id: "JP-TSU",
    countryCode: "JP",
    name: {
      en: "Tsushima",
      ko: "대마도",
      ja: "対馬",
      "zh-CN": "对马",
      "zh-TW": "對馬",
    },
    // 섬 지리 중심 좌표. 남북 ~70km로 긴 섬이라 radius_km로 권역 전체를 커버
    // (북단 히타카쓰 지점 34.656 ~ 남단 34.08 모두 포함). 사방이 바다라 인접 도시 혼입 없음
    coordinates: { latitude: 34.4, longitude: 129.33 },
    radius_km: 40, // 대마도 전역(중심 ±40km) 커버
    priority: 30,
  },
  {
    id: "JP-SPK",
    countryCode: "JP",
    name: {
      en: "Sapporo",
      ko: "삿포로",
      ja: "札幌",
      "zh-CN": "札幌",
      "zh-TW": "札幌",
    },
    coordinates: { latitude: 43.0642, longitude: 141.347 },
    priority: 5, // 일본 내 5위
  },
  {
    id: "JP-YOK",
    countryCode: "JP",
    name: {
      en: "Yokohama",
      ko: "요코하마",
      ja: "横浜",
      "zh-CN": "横滨",
      "zh-TW": "橫濱",
    },
    coordinates: { latitude: 35.4437, longitude: 139.638 },
    priority: 7, // 일본 내 7위
  },
  {
    id: "JP-NAG",
    countryCode: "JP",
    name: {
      en: "Nagoya",
      ko: "나고야",
      ja: "名古屋",
      "zh-CN": "名古屋",
      "zh-TW": "名古屋",
    },
    coordinates: { latitude: 35.1815, longitude: 136.9066 },
    priority: 6, // 일본 내 6위
  },
  {
    id: "JP-KOB",
    countryCode: "JP",
    name: {
      en: "Kobe",
      ko: "고베",
      ja: "神戸",
      "zh-CN": "神户",
      "zh-TW": "神戶",
    },
    coordinates: { latitude: 34.6901, longitude: 135.1955 },
    priority: 8, // 일본 내 8위
  },
  {
    // 미야코지마(오키나와현) — 10-19 검색 지명(area)에서 도시로 승격. prod 렌터카 12개·지점 5곳
    id: "JP-MMY",
    countryCode: "JP",
    name: {
      en: "Miyakojima",
      ko: "미야코지마",
      ja: "宮古島",
      "zh-CN": "宫古岛",
      "zh-TW": "宮古島",
    },
    coordinates: { latitude: 24.8, longitude: 125.3 },
    // 미야코 섬 + 다리로 이어진 이케마·이라부·시모지·구리마(125.13~125.47). 사방이 바다라 이웃 도시 혼입 없음.
    radius_km: 20,
    priority: 9,
  },
  {
    // 이시가키지마(오키나와현 야에야마) — 10-19 검색 지명(area)에서 도시로 승격
    id: "JP-ISG",
    countryCode: "JP",
    name: {
      en: "Ishigaki",
      ko: "이시가키지마",
      ja: "石垣島",
      "zh-CN": "石垣岛",
      "zh-TW": "石垣島",
    },
    coordinates: { latitude: 24.43, longitude: 124.2 },
    radius_km: 22, // 이시가키 섬 전역(24.33~24.61) + 다케토미 섬
    priority: 10,
  },

  // ===== 중국 =====
  {
    id: "CN-BJS",
    countryCode: "CN",
    name: {
      en: "Beijing",
      ko: "베이징",
      ja: "北京",
      "zh-CN": "北京",
      "zh-TW": "北京",
    },
    coordinates: { latitude: 39.9042, longitude: 116.4074 },
    priority: 1,
  },
  {
    id: "CN-SHA",
    countryCode: "CN",
    name: {
      en: "Shanghai",
      ko: "상하이",
      ja: "上海",
      "zh-CN": "上海",
      "zh-TW": "上海",
    },
    coordinates: { latitude: 31.2304, longitude: 121.4737 },
    priority: 2,
  },
  {
    id: "CN-CAN",
    countryCode: "CN",
    name: {
      en: "Guangzhou",
      ko: "광저우",
      ja: "広州",
      "zh-CN": "广州",
      "zh-TW": "廣州",
    },
    coordinates: { latitude: 23.1291, longitude: 113.2644 },
    priority: 3,
  },
  {
    id: "CN-SZX",
    countryCode: "CN",
    name: {
      en: "Shenzhen",
      ko: "선전",
      ja: "深圳",
      "zh-CN": "深圳",
      "zh-TW": "深圳",
    },
    coordinates: { latitude: 22.5431, longitude: 114.0579 },
    priority: 4,
  },
  {
    id: "CN-HGH",
    countryCode: "CN",
    name: {
      en: "Hangzhou",
      ko: "항저우",
      ja: "杭州",
      "zh-CN": "杭州",
      "zh-TW": "杭州",
    },
    coordinates: { latitude: 30.2741, longitude: 120.1551 },
    priority: 5,
  },
  {
    id: "CN-CTU",
    countryCode: "CN",
    name: {
      en: "Chengdu",
      ko: "청두",
      ja: "成都",
      "zh-CN": "成都",
      "zh-TW": "成都",
    },
    coordinates: { latitude: 30.5728, longitude: 104.0668 },
  },
  {
    id: "CN-SIA",
    countryCode: "CN",
    name: {
      en: "Xi'an",
      ko: "시안",
      ja: "西安",
      "zh-CN": "西安",
      "zh-TW": "西安",
    },
    coordinates: { latitude: 34.3416, longitude: 108.9398 },
    priority: 6,
  },

  // ===== 대만 =====
  {
    id: "TW-TPE",
    countryCode: "TW",
    name: {
      en: "Taipei",
      ko: "타이베이",
      ja: "台北",
      "zh-CN": "台北",
      "zh-TW": "台北",
    },
    coordinates: { latitude: 25.033, longitude: 121.5654 },
    priority: 1,
  },
  {
    id: "TW-KHH",
    countryCode: "TW",
    name: {
      en: "Kaohsiung",
      ko: "가오슝",
      ja: "高雄",
      "zh-CN": "高雄",
      "zh-TW": "高雄",
    },
    coordinates: { latitude: 22.6273, longitude: 120.3014 },
    priority: 2,
  },
  {
    id: "TW-TXG",
    countryCode: "TW",
    name: {
      en: "Taichung",
      ko: "타이중",
      ja: "台中",
      "zh-CN": "台中",
      "zh-TW": "台中",
    },
    coordinates: { latitude: 24.1477, longitude: 120.6736 },
    priority: 3,
  },
  {
    id: "TW-TNN",
    countryCode: "TW",
    name: {
      en: "Tainan",
      ko: "타이난",
      ja: "台南",
      "zh-CN": "台南",
      "zh-TW": "台南",
    },
    coordinates: { latitude: 22.9997, longitude: 120.227 },
    priority: 4,
  },
  {
    // 신베이시 — 타이베이를 둘러싼 직할시. 중심=반차오(시청). 단수이·스펀·예류는 search-places/tw
    id: "TW-NWT",
    countryCode: "TW",
    name: {
      en: "New Taipei",
      ko: "신베이",
      ja: "新北",
      "zh-CN": "新北",
      "zh-TW": "新北",
    },
    coordinates: { latitude: 25.0117, longitude: 121.4627 },
    priority: 6,
  },
  {
    // 타오위안시 — 중심=타오위안구. 반경 12km 박스가 타오위안 공항(북서 11km)까지 덮는다
    id: "TW-TAO",
    countryCode: "TW",
    name: {
      en: "Taoyuan",
      ko: "타오위안",
      ja: "桃園",
      "zh-CN": "桃园",
      "zh-TW": "桃園",
    },
    coordinates: { latitude: 24.9936, longitude: 121.301 },
    priority: 5,
  },
  {
    // 타이완 타오위안 국제공항(TPE) — 유심·와이파이 수령 지점형
    id: "TW-TYA",
    countryCode: "TW",
    name: {
      en: "Taoyuan Airport",
      ko: "타오위안공항",
      ja: "桃園空港",
      "zh-CN": "桃园机场",
      "zh-TW": "桃園機場",
    },
    coordinates: { latitude: 25.0797, longitude: 121.2342 },
    radius_km: 3, // 공항 핵심부만(인천공항 KR-ICA 와 같은 지점형). 검색 박스 ±3km.
    mapZoom: 13,
    priority: 5,
  },
  {
    id: "TW-KEE",
    countryCode: "TW",
    name: {
      en: "Keelung",
      ko: "지룽",
      ja: "基隆",
      "zh-CN": "基隆",
      "zh-TW": "基隆",
    },
    coordinates: { latitude: 25.1276, longitude: 121.7392 },
    priority: 9,
  },
  {
    // 신주시(市). 주변 신주현은 search-places/areas(area:TW-HSQ)
    id: "TW-HSZ",
    countryCode: "TW",
    name: {
      en: "Hsinchu",
      ko: "신주",
      ja: "新竹",
      "zh-CN": "新竹",
      "zh-TW": "新竹",
    },
    coordinates: { latitude: 24.8138, longitude: 120.9675 },
    priority: 10,
  },
  {
    // 이란(宜蘭) 시내 — 현 전체는 area:TW-ILA
    id: "TW-YIL",
    countryCode: "TW",
    name: {
      en: "Yilan",
      ko: "이란",
      ja: "宜蘭",
      "zh-CN": "宜兰",
      "zh-TW": "宜蘭",
    },
    coordinates: { latitude: 24.757, longitude: 121.7533 },
    radius_km: 15, // 이란 시내 + 자오시 온천(북 8km)·뤄둥(남 9km)
    priority: 8,
  },
  {
    // 화롄 시내 — 현 전체는 area:TW-HUA
    id: "TW-HUN",
    countryCode: "TW",
    name: {
      en: "Hualien",
      ko: "화롄",
      ja: "花蓮",
      "zh-CN": "花莲",
      "zh-TW": "花蓮",
    },
    coordinates: { latitude: 23.9872, longitude: 121.6016 },
    radius_km: 20, // 화롄 시내 + 치싱탄 + 타이루거 협곡 입구(북 19km)
    priority: 7,
  },
  {
    // 타이둥 시내 — 현 전체(뤼다오·란위 포함)는 area:TW-TTT
    id: "TW-TTT",
    countryCode: "TW",
    name: {
      en: "Taitung",
      ko: "타이둥",
      ja: "台東",
      "zh-CN": "台东",
      "zh-TW": "臺東",
    },
    coordinates: { latitude: 22.7583, longitude: 121.1444 },
    radius_km: 15,
    priority: 11,
  },
  {
    // 컨딩(핑둥현 헝춘 반도) — 핑둥 시내가 아니라 여행지 쪽을 도시로 둔다. 현 전체는 area:TW-PIF
    id: "TW-KEN",
    countryCode: "TW",
    name: {
      en: "Kenting",
      ko: "컨딩",
      ja: "墾丁",
      "zh-CN": "垦丁",
      "zh-TW": "墾丁",
    },
    coordinates: { latitude: 21.9483, longitude: 120.7798 },
    radius_km: 15, // 헝춘 반도 — 헝춘 고성(북 6km)·난완·어롼비 등대(동남 10km)
    priority: 7,
  },
  {
    // 자이시(市). 자이현·아리산은 search-places/areas
    id: "TW-CYI",
    countryCode: "TW",
    name: {
      en: "Chiayi",
      ko: "자이",
      ja: "嘉義",
      "zh-CN": "嘉义",
      "zh-TW": "嘉義",
    },
    coordinates: { latitude: 23.4801, longitude: 120.4491 },
    priority: 12,
  },
  {
    // 르웨탄(일월담, 난터우현) — 난터우현 전체는 area:TW-NAN
    id: "TW-SML",
    countryCode: "TW",
    name: {
      en: "Sun Moon Lake",
      ko: "르웨탄",
      ja: "日月潭",
      "zh-CN": "日月潭",
      "zh-TW": "日月潭",
    },
    coordinates: { latitude: 23.8576, longitude: 120.915 },
    radius_km: 8, // 호수 둘레(수이서·이다사오 부두) — 난터우 시내(25km 서쪽)는 넣지 않는다
    priority: 8,
  },

  // ===== 홍콩 =====
  {
    id: "HK-HKG",
    countryCode: "HK",
    name: {
      en: "Hong Kong",
      ko: "홍콩",
      ja: "香港",
      "zh-CN": "香港",
      "zh-TW": "香港",
    },
    coordinates: { latitude: 22.3193, longitude: 114.1694 },
    priority: 1,
  },

  // ===== 태국 =====
  {
    id: "TH-BKK",
    countryCode: "TH",
    name: {
      en: "Bangkok",
      ko: "방콕",
      ja: "バンコク",
      "zh-CN": "曼谷",
      "zh-TW": "曼谷",
      th: "กรุงเทพมหานคร",
    },
    coordinates: { latitude: 13.7563, longitude: 100.5018 },
    priority: 1,
  },
  {
    id: "TH-CNX",
    countryCode: "TH",
    name: {
      en: "Chiang Mai",
      ko: "치앙마이",
      ja: "チェンマイ",
      "zh-CN": "清迈",
      "zh-TW": "清邁",
      th: "เชียงใหม่",
    },
    coordinates: { latitude: 18.7883, longitude: 98.9853 },
    priority: 2,
  },
  {
    id: "TH-HKT",
    countryCode: "TH",
    name: {
      en: "Phuket",
      ko: "푸켓",
      ja: "プーケット",
      "zh-CN": "普吉",
      "zh-TW": "普吉",
      th: "ภูเก็ต",
    },
    coordinates: { latitude: 7.8804, longitude: 98.3923 },
    priority: 3,
  },
  {
    id: "TH-PYX",
    countryCode: "TH",
    name: {
      en: "Pattaya",
      ko: "파타야",
      ja: "パタヤ",
      "zh-CN": "芭提雅",
      "zh-TW": "芭達雅",
      th: "พัทยา",
    },
    coordinates: { latitude: 12.9236, longitude: 100.8825 },
    priority: 4,
  },
  {
    id: "TH-KBV",
    countryCode: "TH",
    name: {
      en: "Krabi",
      ko: "끄라비",
      ja: "クラビ",
      "zh-CN": "甲米",
      "zh-TW": "喀比",
      th: "กระบี่",
    },
    coordinates: { latitude: 8.0863, longitude: 98.9063 },
    priority: 5,
  },
  {
    id: "TH-HHQ",
    countryCode: "TH",
    name: {
      en: "Hua Hin",
      ko: "후아힌",
      ja: "ホアヒン",
      "zh-CN": "华欣",
      "zh-TW": "華欣",
      th: "หัวหิน",
    },
    coordinates: { latitude: 12.5683, longitude: 99.9576 },
    priority: 6,
  },

  // ===== 베트남 =====
  {
    id: "VN-SGN",
    countryCode: "VN",
    name: {
      en: "Ho Chi Minh City",
      ko: "호치민",
      ja: "ホーチミン",
      "zh-CN": "胡志明市",
      "zh-TW": "胡志明市",
      vi: "Thành phố Hồ Chí Minh",
    },
    coordinates: { latitude: 10.8231, longitude: 106.6297 },
    priority: 1,
  },
  {
    id: "VN-HAN",
    countryCode: "VN",
    name: {
      en: "Hanoi",
      ko: "하노이",
      ja: "ハノイ",
      "zh-CN": "河内",
      "zh-TW": "河內",
      vi: "Hà Nội",
    },
    coordinates: { latitude: 21.0285, longitude: 105.8542 },
    priority: 2,
  },
  {
    id: "VN-DAD",
    countryCode: "VN",
    name: {
      en: "Da Nang",
      ko: "다낭",
      ja: "ダナン",
      "zh-CN": "岘港",
      "zh-TW": "峴港",
      vi: "Đà Nẵng",
    },
    coordinates: { latitude: 16.0544, longitude: 108.2022 },
    priority: 3,
  },
  {
    id: "VN-NHA",
    countryCode: "VN",
    name: {
      en: "Nha Trang",
      ko: "나트랑",
      ja: "ニャチャン",
      "zh-CN": "芽庄",
      "zh-TW": "芽莊",
      vi: "Nha Trang",
    },
    coordinates: { latitude: 12.2388, longitude: 109.1967 },
    priority: 4,
  },
  {
    id: "VN-HOI",
    countryCode: "VN",
    name: {
      en: "Hoi An",
      ko: "호이안",
      ja: "ホイアン",
      "zh-CN": "会安",
      "zh-TW": "會安",
      vi: "Hội An",
    },
    coordinates: { latitude: 15.8801, longitude: 108.338 },
    priority: 5,
  },
  {
    id: "VN-HUE",
    countryCode: "VN",
    name: {
      en: "Hue",
      ko: "후에",
      ja: "フエ",
      "zh-CN": "顺化",
      "zh-TW": "順化",
      vi: "Huế",
    },
    coordinates: { latitude: 16.4637, longitude: 107.5909 },
    priority: 6,
  },

  // ===== 싱가포르 =====
  {
    id: "SG-SIN",
    countryCode: "SG",
    name: {
      en: "Singapore",
      ko: "싱가포르",
      ja: "シンガポール",
      "zh-CN": "新加坡",
      "zh-TW": "新加坡",
    },
    coordinates: { latitude: 1.3521, longitude: 103.8198 },
    priority: 1,
  },

  // ===== 말레이시아 =====
  {
    id: "MY-KUL",
    countryCode: "MY",
    name: {
      en: "Kuala Lumpur",
      ko: "쿠알라룸푸르",
      ja: "クアラルンプール",
      "zh-CN": "吉隆坡",
      "zh-TW": "吉隆坡",
    },
    coordinates: { latitude: 3.139, longitude: 101.6869 },
    priority: 1,
  },
  {
    id: "MY-PEN",
    countryCode: "MY",
    name: {
      en: "Penang",
      ko: "페낭",
      ja: "ペナン",
      "zh-CN": "槟城",
      "zh-TW": "檳城",
    },
    coordinates: { latitude: 5.4164, longitude: 100.3327 },
    priority: 2,
  },
  {
    id: "MY-JHB",
    countryCode: "MY",
    name: {
      en: "Johor Bahru",
      ko: "조호르바루",
      ja: "ジョホールバル",
      "zh-CN": "新山",
      "zh-TW": "新山",
    },
    coordinates: { latitude: 1.4927, longitude: 103.7414 },
    priority: 3,
  },
  {
    id: "MY-MKZ",
    countryCode: "MY",
    name: {
      en: "Malacca",
      ko: "말라카",
      ja: "マラッカ",
      "zh-CN": "马六甲",
      "zh-TW": "馬六甲",
    },
    coordinates: { latitude: 2.1896, longitude: 102.2501 },
    priority: 4,
  },
  {
    id: "MY-BKI",
    countryCode: "MY",
    name: {
      en: "Kota Kinabalu",
      ko: "코타키나발루",
      ja: "コタキナバル",
      "zh-CN": "亚庇",
      "zh-TW": "亞庇",
    },
    coordinates: { latitude: 5.9804, longitude: 116.0735 },
    priority: 5,
  },

  // ===== 인도네시아 =====
  {
    id: "ID-JKT",
    countryCode: "ID",
    name: {
      en: "Jakarta",
      ko: "자카르타",
      ja: "ジャカルタ",
      "zh-CN": "雅加达",
      "zh-TW": "雅加達",
    },
    coordinates: { latitude: -6.2088, longitude: 106.8456 },
    priority: 1,
  },
  {
    id: "ID-DPS",
    countryCode: "ID",
    name: {
      en: "Bali",
      ko: "발리",
      ja: "バリ",
      "zh-CN": "巴厘岛",
      "zh-TW": "峇里島",
    },
    coordinates: { latitude: -8.3405, longitude: 115.092 },
    priority: 2,
  },
  {
    id: "ID-SUB",
    countryCode: "ID",
    name: {
      en: "Surabaya",
      ko: "수라바야",
      ja: "スラバヤ",
      "zh-CN": "泗水",
      "zh-TW": "泗水",
    },
    coordinates: { latitude: -7.2575, longitude: 112.7521 },
    priority: 3,
  },
  {
    id: "ID-BDO",
    countryCode: "ID",
    name: {
      en: "Bandung",
      ko: "반둥",
      ja: "バンドン",
      "zh-CN": "万隆",
      "zh-TW": "萬隆",
    },
    coordinates: { latitude: -6.9175, longitude: 107.6191 },
    priority: 4,
  },
  {
    id: "ID-JOG",
    countryCode: "ID",
    name: {
      en: "Yogyakarta",
      ko: "족자카르타",
      ja: "ジョグジャカルタ",
      "zh-CN": "日惹",
      "zh-TW": "日惹",
    },
    coordinates: { latitude: -7.7956, longitude: 110.3695 },
    priority: 5,
  },

  // ===== 필리핀 =====
  {
    id: "PH-MNL",
    countryCode: "PH",
    name: {
      en: "Manila",
      ko: "마닐라",
      ja: "マニラ",
      "zh-CN": "马尼拉",
      "zh-TW": "馬尼拉",
    },
    coordinates: { latitude: 14.5995, longitude: 120.9842 },
  },
  {
    id: "PH-CEB",
    countryCode: "PH",
    name: {
      en: "Cebu",
      ko: "세부",
      ja: "セブ",
      "zh-CN": "宿务",
      "zh-TW": "宿霧",
    },
    coordinates: { latitude: 10.3157, longitude: 123.8854 },
  },
  {
    id: "PH-DVO",
    countryCode: "PH",
    name: {
      en: "Davao",
      ko: "다바오",
      ja: "ダバオ",
      "zh-CN": "达沃",
      "zh-TW": "達沃",
    },
    coordinates: { latitude: 7.1907, longitude: 125.4553 },
  },
  {
    id: "PH-BOR",
    countryCode: "PH",
    name: {
      en: "Boracay",
      ko: "보라카이",
      ja: "ボラカイ",
      "zh-CN": "长滩岛",
      "zh-TW": "長灘島",
    },
    coordinates: { latitude: 11.9674, longitude: 121.9248 },
  },

  // ===== 인도 =====
  {
    id: "IN-BOM",
    countryCode: "IN",
    name: {
      en: "Mumbai",
      ko: "뭄바이",
      ja: "ムンバイ",
      "zh-CN": "孟买",
      "zh-TW": "孟買",
    },
    coordinates: { latitude: 19.076, longitude: 72.8777 },
  },
  {
    id: "IN-DEL",
    countryCode: "IN",
    name: {
      en: "Delhi",
      ko: "델리",
      ja: "デリー",
      "zh-CN": "德里",
      "zh-TW": "德里",
    },
    coordinates: { latitude: 28.7041, longitude: 77.1025 },
  },
  {
    id: "IN-BLR",
    countryCode: "IN",
    name: {
      en: "Bangalore",
      ko: "방갈로르",
      ja: "バンガロール",
      "zh-CN": "班加罗尔",
      "zh-TW": "班加羅爾",
    },
    coordinates: { latitude: 12.9716, longitude: 77.5946 },
  },
  {
    id: "IN-GOI",
    countryCode: "IN",
    name: {
      en: "Goa",
      ko: "고아",
      ja: "ゴア",
      "zh-CN": "果阿",
      "zh-TW": "果阿",
    },
    coordinates: { latitude: 15.2993, longitude: 74.124 },
  },
  {
    id: "IN-JAI",
    countryCode: "IN",
    name: {
      en: "Jaipur",
      ko: "자이푸르",
      ja: "ジャイプール",
      "zh-CN": "斋浦尔",
      "zh-TW": "齋浦爾",
    },
    coordinates: { latitude: 26.9124, longitude: 75.7873 },
  },

  // ===== 아랍에미리트 =====
  {
    id: "AE-DXB",
    countryCode: "AE",
    name: {
      en: "Dubai",
      ko: "두바이",
      ja: "ドバイ",
      "zh-CN": "迪拜",
      "zh-TW": "杜拜",
    },
    coordinates: { latitude: 25.2048, longitude: 55.2708 },
  },
  {
    id: "AE-AUH",
    countryCode: "AE",
    name: {
      en: "Abu Dhabi",
      ko: "아부다비",
      ja: "アブダビ",
      "zh-CN": "阿布扎比",
      "zh-TW": "阿布達比",
    },
    coordinates: { latitude: 24.4539, longitude: 54.3773 },
  },

  // ===== 호주 =====
  {
    id: "AU-SYD",
    countryCode: "AU",
    name: {
      en: "Sydney",
      ko: "시드니",
      ja: "シドニー",
      "zh-CN": "悉尼",
      "zh-TW": "雪梨",
    },
    coordinates: { latitude: -33.8688, longitude: 151.2093 },
  },
  {
    id: "AU-MEL",
    countryCode: "AU",
    name: {
      en: "Melbourne",
      ko: "멜버른",
      ja: "メルボルン",
      "zh-CN": "墨尔本",
      "zh-TW": "墨爾本",
    },
    coordinates: { latitude: -37.8136, longitude: 144.9631 },
  },

  // ===== 미국 =====
  {
    id: "US-NYC",
    countryCode: "US",
    name: {
      en: "New York",
      ko: "뉴욕",
      ja: "ニューヨーク",
      "zh-CN": "纽约",
      "zh-TW": "紐約",
    },
    coordinates: { latitude: 40.7128, longitude: -74.006 },
  },
  {
    id: "US-LAX",
    countryCode: "US",
    name: {
      en: "Los Angeles",
      ko: "로스앤젤레스",
      ja: "ロサンゼルス",
      "zh-CN": "洛杉矶",
      "zh-TW": "洛杉磯",
    },
    coordinates: { latitude: 34.0522, longitude: -118.2437 },
  },
  {
    id: "US-CHI",
    countryCode: "US",
    name: {
      en: "Chicago",
      ko: "시카고",
      ja: "シカゴ",
      "zh-CN": "芝加哥",
      "zh-TW": "芝加哥",
    },
    coordinates: { latitude: 41.8781, longitude: -87.6298 },
  },
  {
    id: "US-SFO",
    countryCode: "US",
    name: {
      en: "San Francisco",
      ko: "샌프란시스코",
      ja: "サンフランシスコ",
      "zh-CN": "旧金山",
      "zh-TW": "舊金山",
    },
    coordinates: { latitude: 37.7749, longitude: -122.4194 },
  },
  {
    id: "US-LAS",
    countryCode: "US",
    name: {
      en: "Las Vegas",
      ko: "라스베이거스",
      ja: "ラスベガス",
      "zh-CN": "拉斯维加斯",
      "zh-TW": "拉斯維加斯",
    },
    coordinates: { latitude: 36.1699, longitude: -115.1398 },
  },
  {
    id: "US-SEA",
    countryCode: "US",
    name: {
      en: "Seattle",
      ko: "시애틀",
      ja: "シアトル",
      "zh-CN": "西雅图",
      "zh-TW": "西雅圖",
    },
    coordinates: { latitude: 47.6062, longitude: -122.3321 },
  },
  {
    id: "US-MIA",
    countryCode: "US",
    name: {
      en: "Miami",
      ko: "마이애미",
      ja: "マイアミ",
      "zh-CN": "迈阿密",
      "zh-TW": "邁阿密",
    },
    coordinates: { latitude: 25.7617, longitude: -80.1918 },
  },

  // ===== 영국 =====
  {
    id: "GB-LON",
    countryCode: "GB",
    name: {
      en: "London",
      ko: "런던",
      ja: "ロンドン",
      "zh-CN": "伦敦",
      "zh-TW": "倫敦",
    },
    coordinates: { latitude: 51.5074, longitude: -0.1278 },
  },
  {
    id: "GB-MAN",
    countryCode: "GB",
    name: {
      en: "Manchester",
      ko: "맨체스터",
      ja: "マンチェスター",
      "zh-CN": "曼彻斯特",
      "zh-TW": "曼徹斯特",
    },
    coordinates: { latitude: 53.4808, longitude: -2.2426 },
  },

  // ===== 프랑스 =====
  {
    id: "FR-PAR",
    countryCode: "FR",
    name: {
      en: "Paris",
      ko: "파리",
      ja: "パリ",
      "zh-CN": "巴黎",
      "zh-TW": "巴黎",
    },
    coordinates: { latitude: 48.8566, longitude: 2.3522 },
  },
  {
    id: "FR-NCE",
    countryCode: "FR",
    name: {
      en: "Nice",
      ko: "니스",
      ja: "ニース",
      "zh-CN": "尼斯",
      "zh-TW": "尼斯",
    },
    coordinates: { latitude: 43.7102, longitude: 7.262 },
  },

  // ===== 독일 =====
  {
    id: "DE-BER",
    countryCode: "DE",
    name: {
      en: "Berlin",
      ko: "베를린",
      ja: "ベルリン",
      "zh-CN": "柏林",
      "zh-TW": "柏林",
    },
    coordinates: { latitude: 52.52, longitude: 13.405 },
  },
  {
    id: "DE-MUC",
    countryCode: "DE",
    name: {
      en: "Munich",
      ko: "뮌헨",
      ja: "ミュンヘン",
      "zh-CN": "慕尼黑",
      "zh-TW": "慕尼黑",
    },
    coordinates: { latitude: 48.1351, longitude: 11.582 },
  },

  // ===== 이탈리아 =====
  {
    id: "IT-ROM",
    countryCode: "IT",
    name: {
      en: "Rome",
      ko: "로마",
      ja: "ローマ",
      "zh-CN": "罗马",
      "zh-TW": "羅馬",
    },
    coordinates: { latitude: 41.9028, longitude: 12.4964 },
  },
  {
    id: "IT-MIL",
    countryCode: "IT",
    name: {
      en: "Milan",
      ko: "밀라노",
      ja: "ミラノ",
      "zh-CN": "米兰",
      "zh-TW": "米蘭",
    },
    coordinates: { latitude: 45.4642, longitude: 9.19 },
  },
  {
    id: "IT-VCE",
    countryCode: "IT",
    name: {
      en: "Venice",
      ko: "베니스",
      ja: "ヴェネツィア",
      "zh-CN": "威尼斯",
      "zh-TW": "威尼斯",
    },
    coordinates: { latitude: 45.4408, longitude: 12.3155 },
  },

  // ===== 스페인 =====
  {
    id: "ES-MAD",
    countryCode: "ES",
    name: {
      en: "Madrid",
      ko: "마드리드",
      ja: "マドリード",
      "zh-CN": "马德里",
      "zh-TW": "馬德里",
    },
    coordinates: { latitude: 40.4168, longitude: -3.7038 },
  },
  {
    id: "ES-BCN",
    countryCode: "ES",
    name: {
      en: "Barcelona",
      ko: "바르셀로나",
      ja: "バルセロナ",
      "zh-CN": "巴塞罗那",
      "zh-TW": "巴塞隆納",
    },
    coordinates: { latitude: 41.3851, longitude: 2.1734 },
  },
];

/**
 * 도시 전체 — 기존 도시(행정구역 소속을 더함) + 10-19 여행지 확장(destinations.generated).
 * 기존 도시가 앞에 있어 이름 조회(getCityBounds 등)가 같은 표기에서 기존 도시를 먼저 찾는다.
 */
export const CITIES: City[] = [
  ...BASE_CITIES.map((city) =>
    EXISTING_CITY_MEMBERSHIP[city.id]
      ? { ...city, membership: EXISTING_CITY_MEMBERSHIP[city.id] }
      : city,
  ),
  ...DESTINATION_CITIES,
];

// ===== Helper Functions =====

/**
 * 국가 코드로 도시 목록 가져오기
 */
export function getCitiesByCountry(countryCode: string): City[] {
  return CITIES.filter((city) => city.countryCode === countryCode);
}

/**
 * 국가 검색
 */
export function searchCountries(
  query: string,
  locale: Locale = "ko",
): Country[] {
  const lowerQuery = query.toLowerCase().trim();
  if (!lowerQuery) return COUNTRIES;

  return COUNTRIES.filter((country) => {
    const name = (country.name[locale] || country.name.en).toLowerCase();
    return name.includes(lowerQuery);
  });
}

/**
 * 도시 검색
 */
export function searchCities(
  query: string,
  locale: Locale = "ko",
  countryCode?: string,
): City[] {
  const lowerQuery = query.toLowerCase().trim();
  const filteredCities = countryCode ? getCitiesByCountry(countryCode) : CITIES;

  if (!lowerQuery) return filteredCities;

  return filteredCities.filter((city) => {
    const name = (city.name[locale] || city.name.en).toLowerCase();
    return name.includes(lowerQuery);
  });
}

/**
 * 도시 ID로 도시 찾기
 */
export function getCityById(cityId: string): City | undefined {
  return CITIES.find((city) => city.id === cityId);
}

/**
 * 국가 코드로 국가 찾기
 */
export function getCountryByCode(countryCode: string): Country | undefined {
  return COUNTRIES.find((country) => country.code === countryCode);
}

/**
 * 좌표가 있는 도시만 필터링
 */
export function getCitiesWithCoordinates(countryCode?: string): City[] {
  const cities = countryCode ? getCitiesByCountry(countryCode) : CITIES;
  return cities.filter((city) => city.coordinates);
}

// 기존 major-cities.ts 호환을 위한 타입
export interface MajorCity {
  name: string;
  nameTranslations?: {
    ko?: string;
    ja?: string;
    "zh-CN"?: string;
    "zh-TW"?: string;
    th?: string;
    vi?: string;
  };
  latitude: number;
  longitude: number;
  priority?: number; // 인기순 정렬용 (낮을수록 상위)
}

/**
 * 기존 MAJOR_CITIES 형식으로 변환 (하위 호환성)
 */
export function getMajorCitiesByCountry(countryCode: string): MajorCity[] {
  return getCitiesWithCoordinates(countryCode).map((city) => ({
    name: city.name.en,
    nameTranslations: {
      ko: city.name.ko,
      ja: city.name.ja,
      "zh-CN": city.name["zh-CN"],
      "zh-TW": city.name["zh-TW"],
      th: city.name.th,
      vi: city.name.vi,
    },
    latitude: city.coordinates!.latitude,
    longitude: city.coordinates!.longitude,
    priority: city.priority,
  }));
}

/**
 * MAJOR_CITIES object 형식 (하위 호환성 - Record<string, MajorCity[]>)
 */
export const MAJOR_CITIES: Record<string, MajorCity[]> = COUNTRIES.reduce(
  (acc, country) => {
    const cities = getMajorCitiesByCountry(country.code);
    if (cities.length > 0) {
      acc[country.code] = cities;
    }
    return acc;
  },
  {} as Record<string, MajorCity[]>,
);

/**
 * 한국/일본 위치명 정규화 (접미사 제거)
 * - 한국: 서울특별시 → 서울, 경기도 → 경기
 * - 일본: 도쿄도 → 도쿄, 오사카부 → 오사카 (홋카이도 예외)
 *
 * Google Geocoding API의 short_name이 접미사를 제거하지 않는 경우에 대한 fallback
 */
function normalizeLocationName(name: string): string {
  // 홋카이도는 예외 (도가 지명의 일부)
  if (name === "홋카이도" || name === "北海道") {
    return name;
  }

  // 한국 행정구역 접미사 제거
  const koreanNormalized = name.replace(
    /(특별시|광역시|특별자치시|특별자치도|도)$/,
    "",
  );
  if (koreanNormalized !== name) {
    return koreanNormalized;
  }

  // 일본 도도부현 접미사 제거
  const japaneseNormalized = name.replace(/(都|府|県)$/, "");
  if (japaneseNormalized !== name) {
    return japaneseNormalized;
  }

  return name;
}

/**
 * 한글 도시명을 다국어로 번역
 * @param koreanCityName 한글 도시명 (예: "서울", "부산")
 * @param targetLocale 목표 언어 코드 (en, ko, ja, zh, zh-TW 등)
 * @returns 번역된 도시명 (없으면 원본 반환)
 */
/**
 * Resolve any city/country name (in any of 5 locales, or English slug) to the
 * target locale. Used for SEO metadata where the URL slug can come in many
 * shapes — `/ja/s/seoul`, `/ja/s/ソウル`, `/ja/s/서울` — and we always want the
 * page title in the user's language.
 */
export function getLocalizedRegionName(
  decodedSlug: string,
  targetLocale: Locale,
): string {
  if (!decodedSlug) return decodedSlug;
  const lower = decodedSlug.toLowerCase();

  // 경로 슬러그는 placeSlug(name.en)로 만들어진다("South Korea" → "south-korea",
  // "New York" → "new-york"). 이름 문자열끼리만 비교하면 하이픈 때문에 여러 단어
  // 지명이 전부 미스나고("seoul"처럼 한 단어만 우연히 맞았다) URL 슬러그가 제목·헤딩에
  // 그대로 노출된다. 그래서 이름 비교에 더해 양쪽을 슬러그로 바꿔 한 번 더 맞춰 본다.
  const slugOfInput = placeSlug(decodedSlug);
  const matchesAnyName = (name: City["name"] | Country["name"]): boolean =>
    Object.values(name).some(
      (n) => typeof n === "string" && (n === decodedSlug || n.toLowerCase() === lower),
    ) ||
    (typeof name.en === "string" &&
      slugOfInput.length > 0 &&
      placeSlug(name.en) === slugOfInput);

  const city = CITIES.find((c) => matchesAnyName(c.name));
  if (city) {
    return city.name[targetLocale] || city.name.en || city.name.ko;
  }

  const country = COUNTRIES.find((c) => matchesAnyName(c.name));
  if (country) {
    return country.name[targetLocale] || country.name.en || country.name.ko;
  }

  return decodedSlug;
}

export function translateCityName(
  koreanCityName: string,
  targetLocale: Locale = "ko",
): string {
  // 1차: 직접 매칭 시도
  let city = CITIES.find((c) => c.name.ko === koreanCityName);

  // 2차: 정규화 후 매칭 (서울특별시 → 서울 등)
  if (!city) {
    const normalized = normalizeLocationName(koreanCityName);
    city = CITIES.find((c) => c.name.ko === normalized);
  }

  if (!city) {
    // 매칭되는 도시가 없으면 원본 반환하되, 라틴 문자(영문)는 앞 글자를
    // 대문자로 올려 "seoul" 같은 소문자 데이터가 그대로 노출되지 않게 한다.
    const first = koreanCityName.charAt(0);
    return first >= "a" && first <= "z"
      ? first.toUpperCase() + koreanCityName.slice(1)
      : koreanCityName;
  }

  // 목표 언어로 번역, 없으면 영어, 그것도 없으면 한글
  return city.name[targetLocale] || city.name.en || city.name.ko;
}

/**
 * 여러 도시명을 한번에 번역
 * @param cityNames 한글 도시명 배열
 * @param targetLocale 목표 언어 코드
 * @returns 번역된 도시명 배열
 */
export function translateCityNames(
  cityNames: string[],
  targetLocale: Locale = "ko",
): string[] {
  return cityNames.map((name) => translateCityName(name, targetLocale));
}

// ===== 인기 여행지 설정 =====

/**
 * locale에 맞는 인기 여행지 도시 목록 가져오기 (상품 조회용)
 * HOT_PLACE_CITY_IDS에서 COMING_SOON 도시를 제외하여 실제 상품이 있는 도시만 반환
 * @param locale 언어 코드
 * @returns 인기 여행지 City 배열
 */
export function getPopularDestinations(locale: string): City[] {
  const cityIds = HOT_PLACE_CITY_IDS[locale] || HOT_PLACE_CITY_IDS["en"];
  return cityIds
    .filter((id) => !COMING_SOON_CITY_IDS.has(id))
    .map((id) => getCityById(id))
    .filter(Boolean) as City[];
}

/**
 * ISO 국가 코드 → locale 매핑
 * 사용자 위치 기반 인기 도시 추천에 사용
 */
const COUNTRY_TO_LOCALE: Record<string, string> = {
  KR: "ko",
  JP: "ja",
  CN: "zh-CN",
  TW: "zh-TW",
  HK: "zh-TW",
};

/**
 * 국가 코드 기반 인기 여행지 도시 목록 가져오기
 * 사용자의 실제 위치(국가)에 따라 관련성 높은 도시를 추천
 * @param countryCode ISO 2자리 국가 코드 (예: 'KR', 'JP') 또는 null
 * @param fallbackLocale 국가 감지 실패시 사용할 locale
 * @returns 인기 여행지 City 배열
 */
export function getPopularDestinationsByCountry(
  countryCode: string | null,
  fallbackLocale: string,
): City[] {
  const targetLocale = countryCode
    ? COUNTRY_TO_LOCALE[countryCode] || "en"
    : fallbackLocale;

  return getPopularDestinations(targetLocale);
}

/**
 * 국가 코드에서 대응하는 locale 가져오기
 * @param countryCode ISO 2자리 국가 코드 (예: 'KR', 'JP')
 * @returns 대응하는 locale 또는 null
 */
export function getLocaleFromCountry(
  countryCode: string | null,
): string | null {
  if (!countryCode) return null;
  return COUNTRY_TO_LOCALE[countryCode] || null;
}

/**
 * 지원되는 모든 인기 여행지 locale 목록
 */
export function getSupportedDestinationLocales(): string[] {
  return Object.keys(HOT_PLACE_CITY_IDS);
}

// ===== Bounds 관련 =====

export interface Bounds {
  ne_lat: number;
  ne_lng: number;
  sw_lat: number;
  sw_lng: number;
}

/**
 * 도시명으로 검색 bounds 가져오기
 * 도시 중심 좌표에서 ±offset 만큼의 사각형 영역 반환
 * @param cityName 도시명 (다국어 지원)
 * @param locale 언어 코드
 * @param offset 중심에서의 offset (기본 0.1도 = 약 10km)
 */
export function getCityBounds(
  cityName: string,
  locale: Locale = "ko",
  offset?: number, // 미지정 시 city.radius_km 기반(광역 도시 권역 전체), 없으면 0.1°
): Bounds | null {
  // 모든 언어로 도시 검색
  const city = CITIES.find(
    (c) =>
      c.name[locale] === cityName ||
      c.name.ko === cityName ||
      c.name.en === cityName ||
      c.name.ja === cityName ||
      c.name["zh-CN"] === cityName ||
      c.name["zh-TW"] === cityName,
  );

  if (!city?.coordinates) return null;

  // 명시 offset 우선 → 없으면 radius_km(도 단위 환산: km/111)로 대마도·오사카 등 광역권
  // 전체를 포괄, radius_km도 없으면 공통 기본값(DEFAULT_CITY_RADIUS_KM).
  const off = offset ?? (city.radius_km ?? DEFAULT_CITY_RADIUS_KM) / 111;

  return {
    ne_lat: city.coordinates.latitude + off,
    ne_lng: city.coordinates.longitude + off,
    sw_lat: city.coordinates.latitude - off,
    sw_lng: city.coordinates.longitude - off,
  };
}

/**
 * 좌표가 bounds 내에 있는지 확인
 */
export function isWithinBounds(
  lat: number,
  lng: number,
  bounds: Bounds,
): boolean {
  return (
    lat >= bounds.sw_lat &&
    lat <= bounds.ne_lat &&
    lng >= bounds.sw_lng &&
    lng <= bounds.ne_lng
  );
}

/**
 * 도시 중심 좌표 가져오기
 */
export function getCityCenter(
  cityName: string,
  locale: Locale = "ko",
): { lat: number; lng: number } | null {
  const city = CITIES.find(
    (c) =>
      c.name[locale] === cityName ||
      c.name.ko === cityName ||
      c.name.en === cityName,
  );

  if (!city?.coordinates) return null;

  return {
    lat: city.coordinates.latitude,
    lng: city.coordinates.longitude,
  };
}

/**
 * 도시 검색 bounds offset(도 단위) 가져오기.
 * 도시에 radius_km가 설정돼 있으면 그 값을 도 단위로 환산(km/111), 아니면 defaultOffset.
 * 대마도(섬 전역)·오사카(광역권)처럼 권역이 넓은 도시를 위해 사용.
 */
export function getCityBoundsOffset(
  cityName: string,
  locale: Locale = "ko",
  defaultOffset = DEFAULT_CITY_RADIUS_KM / 111,
): number {
  const city = CITIES.find(
    (c) =>
      c.name[locale] === cityName ||
      c.name.ko === cityName ||
      c.name.en === cityName,
  );

  if (city?.radius_km) return city.radius_km / 111; // km → 위도 1도 ≈ 111km
  return defaultOffset;
}

/**
 * 도시 검색 시 지도 초기 줌 레벨 가져오기.
 * radius_km가 설정된 광역 도시(대마도 섬 전역·오사카 광역권 등)는 권역 전체가 보이도록 줌을 낮춤.
 * 일반 도시는 undefined를 반환해 MapView 기본 줌(12)을 사용.
 */
export function getCityMapZoom(
  cityName: string,
  locale: Locale = "ko",
): number | undefined {
  const city = CITIES.find(
    (c) =>
      c.name[locale] === cityName ||
      c.name.ko === cityName ||
      c.name.en === cityName,
  );

  // 명시적 mapZoom이 있으면 그대로 사용 (공항 등 지점형, clamp 없음)
  if (city?.mapZoom !== undefined) return city.mapZoom;

  if (!city?.radius_km) return undefined;
  // 반경 40km ≈ 줌 10 기준(권역이 화면을 적당히 채움), 반경이 2배가 되면 줌 -1
  const zoom = Math.round(10 + Math.log2(40 / city.radius_km));
  return Math.max(6, Math.min(12, zoom));
}

/**
 * 국가별 대표 좌표 및 줌 레벨 (국가 전체 검색용)
 * COUNTRIES 배열의 모든 국가에 대응
 */
export const COUNTRY_CENTERS: Record<
  string,
  { lat: number; lng: number; zoom: number }
> = {
  KR: { lat: 36.5, lng: 127.5, zoom: 7 },
  JP: { lat: 36.5, lng: 138.0, zoom: 6 },
  CN: { lat: 35.0, lng: 105.0, zoom: 4 },
  TW: { lat: 23.7, lng: 121.0, zoom: 7 },
  HK: { lat: 22.3, lng: 114.17, zoom: 11 },
  TH: { lat: 13.75, lng: 100.5, zoom: 6 },
  VN: { lat: 16.0, lng: 108.0, zoom: 6 },
  SG: { lat: 1.35, lng: 103.82, zoom: 11 },
  MY: { lat: 4.2, lng: 109.5, zoom: 6 },
  ID: { lat: -2.5, lng: 118.0, zoom: 5 },
  PH: { lat: 12.8, lng: 121.8, zoom: 6 },
  IN: { lat: 20.6, lng: 79.0, zoom: 5 },
  AE: { lat: 24.0, lng: 54.0, zoom: 7 },
  AU: { lat: -25.3, lng: 133.8, zoom: 4 },
  NZ: { lat: -41.3, lng: 174.8, zoom: 6 },
  US: { lat: 39.8, lng: -98.6, zoom: 4 },
  GB: { lat: 54.0, lng: -2.0, zoom: 6 },
  FR: { lat: 46.6, lng: 2.3, zoom: 6 },
  DE: { lat: 51.2, lng: 10.4, zoom: 6 },
  IT: { lat: 42.5, lng: 12.5, zoom: 6 },
  ES: { lat: 40.0, lng: -3.7, zoom: 6 },
};

/**
 * 국가 코드로 중심 좌표 + 줌 레벨 가져오기
 */
export function getCountryCenter(
  countryCode: string,
): { lat: number; lng: number; zoom: number } | null {
  return COUNTRY_CENTERS[countryCode] || null;
}

// ===== 핫플레이스 (Hot Places) 설정 =====

/**
 * 핫플레이스 도시 이미지 URL (Unsplash)
 * 도시 ID를 키로 사용
 */
export const HOT_PLACE_IMAGES: Record<string, string> = {
  // 한국
  "KR-SEL": "/images/destinations/kr-sel-locarora-v2.webp", // 서울
  "KR-PUS": "/images/destinations/kr-pus-locarora-v2.webp", // 부산
  "KR-JEJ": "/images/destinations/kr-jej-locarora-v2.webp", // 제주
  "KR-ICN": "/images/destinations/kr-icn.webp", // 인천
  "KR-ICA": "/images/destinations/kr-ica-locarora-v2.webp", // 인천공항
  // 일본
  "JP-TYO": "/images/destinations/jp-tyo-locarora-v3.webp", // 도쿄
  "JP-OSA": "/images/destinations/jp-osa-locarora-v3.webp", // 오사카
  "JP-KYO": "/images/destinations/jp-kyo-locarora-v2.webp", // 교토
  "JP-FUK": "/images/destinations/jp-fuk.jpg", // 후쿠오카
  "JP-TSU": "/images/destinations/jp-tsu-locarora-v2.webp", // 대마도
  // 동남아시아
  "TH-BKK": "/images/destinations/th-bkk.jpg", // 방콕
  "SG-SIN": "/images/destinations/sg-sin.jpg", // 싱가포르
  "VN-SGN": "/images/destinations/vn-sgn.jpg", // 호치민
  "VN-DAD": "/images/destinations/vn-dad.jpg", // 다낭
  "ID-DPS": "/images/destinations/id-dps.jpg", // 발리
  "MY-KUL": "/images/destinations/my-kul.jpg", // 쿠알라룸푸르
  "PH-CEB": "/images/destinations/ph-ceb.jpg", // 세부
  // 중국/대만/홍콩
  "CN-SHA": "/images/destinations/cn-sha.jpg", // 상하이
  "CN-BJS": "/images/destinations/cn-bjs.jpg", // 베이징
  "HK-HKG": "/images/destinations/hk-hkg.jpg", // 홍콩
  "TW-TPE": "/images/destinations/tw-tpe.jpg", // 타이베이
  "TW-KHH": "/images/destinations/tw-khh.jpg", // 가오슝
  // 기타 아시아
  "AE-DXB": "/images/destinations/ae-dxb.jpg", // 두바이
  // 오세아니아
  "AU-SYD": "/images/destinations/au-syd.jpg", // 시드니
  // 미국
  "US-NYC": "/images/destinations/us-nyc.webp", // 뉴욕
  "US-LAX": "/images/destinations/us-lax.webp", // 로스앤젤레스
  "US-LAS": "/images/destinations/us-las.jpg", // 라스베이거스
  // 유럽
  "GB-LON": "/images/destinations/gb-lon.jpg", // 런던
  "FR-PAR": "/images/destinations/fr-par.webp", // 파리
  "IT-ROM": "/images/destinations/it-rom.jpg", // 로마
  "ES-BCN": "/images/destinations/es-bcn.jpg", // 바르셀로나
};

/**
 * 핫플레이스 도시 타입 (이미지 URL 포함)
 */
export interface HotPlaceCity extends City {
  imageUrl: string;
  comingSoon?: boolean;
}

/**
 * 아직 서비스가 오픈되지 않은 도시 ID 목록
 * 이 도시들은 인기 여행지에 표시되지만 "준비중" 뱃지가 붙고 클릭이 비활성화됨
 */
export const COMING_SOON_CITY_IDS: Set<string> = new Set();

/**
 * locale별 핫플레이스 도시 ID 목록 (8개)
 * 제주가 후쿠오카를 대체해 마지막 카드에 노출됨.
 */
export const HOT_PLACE_CITY_IDS: Record<string, string[]> = {
  ko: ["KR-SEL", "KR-ICA", "JP-TYO", "JP-OSA", "KR-PUS", "JP-TSU", "JP-KYO", "KR-JEJ"], // 한국 사용자
  ja: ["JP-TYO", "JP-OSA", "KR-SEL", "KR-ICA", "KR-PUS", "JP-TSU", "JP-KYO", "KR-JEJ"], // 일본 사용자
  "zh-CN": ["KR-SEL", "KR-ICA", "JP-TYO", "JP-OSA", "KR-PUS", "JP-TSU", "JP-KYO", "KR-JEJ"], // 중국 사용자
  "zh-TW": ["KR-SEL", "KR-ICA", "JP-TYO", "JP-OSA", "KR-PUS", "JP-TSU", "JP-KYO", "KR-JEJ"], // 대만 사용자
  en: ["KR-SEL", "KR-ICA", "JP-TYO", "JP-OSA", "KR-PUS", "JP-TSU", "JP-KYO", "KR-JEJ"], // 영어권 사용자
  // ja: ["JP-TYO", "JP-OSA", "JP-KYO", "KR-SEL", "KR-ICA"], // 일본 사용자
  // "zh-CN": ["CN-SHA", "CN-BJS", "KR-SEL", "KR-ICA", "JP-TYO"], // 중국 사용자
  // "zh-TW": ["TW-TPE", "TW-KHH", "KR-SEL", "KR-ICA", "JP-TYO"], // 대만 사용자
  // en: ["KR-SEL", "KR-ICA", "JP-TYO", "JP-OSA", "SG-SIN"], // 영어권 사용자
};

/**
 * locale에 맞는 핫플레이스 도시 목록 가져오기 (이미지 URL 포함)
 * @param locale 언어 코드
 * @returns 핫플레이스 도시 배열 (imageUrl 포함)
 */
export function getHotPlaceCities(locale: string): HotPlaceCity[] {
  const cityIds = HOT_PLACE_CITY_IDS[locale] || HOT_PLACE_CITY_IDS["en"];

  return cityIds
    .map((id): HotPlaceCity | null => {
      const city = getCityById(id);
      if (!city) return null;

      return {
        ...city,
        imageUrl: HOT_PLACE_IMAGES[id] || "",
        ...(COMING_SOON_CITY_IDS.has(id) && { comingSoon: true }),
      };
    })
    .filter((city): city is HotPlaceCity => city !== null && !!city.imageUrl);
}

/**
 * 특정 도시의 핫플레이스 이미지 URL 가져오기
 * @param cityId 도시 ID
 * @returns 이미지 URL (없으면 undefined)
 */
export function getHotPlaceImage(cityId: string): string | undefined {
  return HOT_PLACE_IMAGES[cityId];
}

// ===== 핫스팟 (Hot Spots) - 도시 내 인기 지역/관광지 =====

/**
 * 핫스팟 인터페이스 (도시 내 인기 지역/관광지)
 */
export interface HotSpot {
  id: string;
  cityId: string; // 소속 도시 ID
  name: LocalizedPlaceName;
  coordinates: {
    latitude: number;
    longitude: number;
  };
  imageUrl: string;
}

/**
 * 핫스팟 데이터 - 주요 도시별 인기 지역/관광지
 */
export const HOT_SPOTS: HotSpot[] = [
  // ===== 서울 (Seoul) =====
  {
    id: "KR-SEL-HDS",
    cityId: "KR-SEL",
    name: {
      en: "Hongdae/Sinchon",
      ko: "홍대/신촌",
      ja: "弘大/新村",
      "zh-CN": "弘大/新村",
      "zh-TW": "弘大/新村",
    },
    coordinates: { latitude: 37.5563, longitude: 126.9237 },
    imageUrl:
      "https://images.unsplash.com/photo-1601621915196-2621bfb0cd6e?w=800&q=80",
  },
  {
    id: "KR-SEL-JGU",
    cityId: "KR-SEL",
    name: {
      en: "Jung-gu/Myeongdong",
      ko: "중구/명동",
      ja: "中区/明洞",
      "zh-CN": "中区/明洞",
      "zh-TW": "中區/明洞",
    },
    coordinates: { latitude: 37.5636, longitude: 126.9869 },
    imageUrl:
      "https://images.unsplash.com/photo-1534274988757-a28bf1a57c17?w=800&q=80",
  },
  {
    id: "KR-SEL-HAN",
    cityId: "KR-SEL",
    name: {
      en: "Hangang River",
      ko: "한강",
      ja: "漢江",
      "zh-CN": "汉江",
      "zh-TW": "漢江",
    },
    coordinates: { latitude: 37.5173, longitude: 126.9784 },
    imageUrl:
      "https://images.unsplash.com/photo-1546874177-9e664107314e?w=800&q=80",
  },
  {
    id: "KR-SEL-GNS",
    cityId: "KR-SEL",
    name: {
      en: "Gangnam/Sinsa",
      ko: "강남/신사",
      ja: "江南/新沙",
      "zh-CN": "江南/新沙",
      "zh-TW": "江南/新沙",
    },
    coordinates: { latitude: 37.5172, longitude: 127.0286 },
    imageUrl:
      "https://images.unsplash.com/photo-1548115184-bc6544d06a58?w=800&q=80",
  },
  {
    id: "KR-SEL-HNI",
    cityId: "KR-SEL",
    name: {
      en: "Hannam/Itaewon",
      ko: "한남/이태원",
      ja: "漢南/梨泰院",
      "zh-CN": "汉南/梨泰院",
      "zh-TW": "漢南/梨泰院",
    },
    coordinates: { latitude: 37.5345, longitude: 126.9978 },
    imageUrl:
      "https://images.unsplash.com/photo-1565967511849-76a60a516170?w=800&q=80",
  },
  {
    id: "KR-SEL-CDG",
    cityId: "KR-SEL",
    name: {
      en: "Cheongdam/Garosu-gil",
      ko: "청담/가로수길",
      ja: "清潭/カロスキル",
      "zh-CN": "清潭/林荫路",
      "zh-TW": "清潭/林蔭路",
    },
    coordinates: { latitude: 37.5219, longitude: 127.0411 },
    imageUrl:
      "https://images.unsplash.com/photo-1517154421773-0529f29ea451?w=800&q=80",
  },
  {
    id: "KR-SEL-JRS",
    cityId: "KR-SEL",
    name: {
      en: "Jongno/Seochon",
      ko: "종로/서촌",
      ja: "鍾路/西村",
      "zh-CN": "钟路/西村",
      "zh-TW": "鍾路/西村",
    },
    coordinates: { latitude: 37.5759, longitude: 126.9769 },
    imageUrl:
      "https://images.unsplash.com/photo-1546874177-9e664107314e?w=800&q=80",
  },
  {
    id: "KR-SEL-SPA",
    cityId: "KR-SEL",
    name: {
      en: "Songpa/Jamsil",
      ko: "송파/잠실",
      ja: "松坡/蚕室",
      "zh-CN": "松坡/蚕室",
      "zh-TW": "松坡/蠶室",
    },
    coordinates: { latitude: 37.5113, longitude: 127.1052 },
    imageUrl:
      "https://images.unsplash.com/photo-1517154421773-0529f29ea451?w=800&q=80",
  },
  {
    id: "KR-SEL-YID",
    cityId: "KR-SEL",
    name: {
      en: "Yeouido",
      ko: "여의도",
      ja: "汝矣島",
      "zh-CN": "汝矣岛",
      "zh-TW": "汝矣島",
    },
    coordinates: { latitude: 37.5219, longitude: 126.9245 },
    imageUrl:
      "https://images.unsplash.com/photo-1578637387939-43c525550085?w=800&q=80",
  },
  {
    id: "KR-SEL-SGS",
    cityId: "KR-SEL",
    name: {
      en: "Seoul Suburbs",
      ko: "서울근교",
      ja: "ソウル近郊",
      "zh-CN": "首尔近郊",
      "zh-TW": "首爾近郊",
    },
    coordinates: { latitude: 37.6584, longitude: 127.0147 },
    imageUrl:
      "https://images.unsplash.com/photo-1596276020587-8044fe049813?w=800&q=80",
  },

  // ===== 부산 (Busan) =====
  {
    id: "KR-PUS-HUD",
    cityId: "KR-PUS",
    name: {
      en: "Haeundae",
      ko: "해운대",
      ja: "海雲台",
      "zh-CN": "海云台",
      "zh-TW": "海雲台",
    },
    coordinates: { latitude: 35.1587, longitude: 129.1604 },
    imageUrl:
      "https://images.unsplash.com/photo-1569154941061-e231b4725ef1?w=800&q=80",
  },
  {
    id: "KR-PUS-GNL",
    cityId: "KR-PUS",
    name: {
      en: "Gwangalli",
      ko: "광안리",
      ja: "広安里",
      "zh-CN": "广安里",
      "zh-TW": "廣安里",
    },
    coordinates: { latitude: 35.1532, longitude: 129.1186 },
    imageUrl:
      "https://images.unsplash.com/photo-1569154941061-e231b4725ef1?w=800&q=80",
  },
  {
    id: "KR-PUS-NMP",
    cityId: "KR-PUS",
    name: {
      en: "Nampo-dong",
      ko: "남포동",
      ja: "南浦洞",
      "zh-CN": "南浦洞",
      "zh-TW": "南浦洞",
    },
    coordinates: { latitude: 35.0977, longitude: 129.0309 },
    imageUrl:
      "https://images.unsplash.com/photo-1519677100203-a0e668c92439?w=800&q=80",
  },
  {
    id: "KR-PUS-SGU",
    cityId: "KR-PUS",
    name: {
      en: "Seomyeon",
      ko: "서면",
      ja: "西面",
      "zh-CN": "西面",
      "zh-TW": "西面",
    },
    coordinates: { latitude: 35.1578, longitude: 129.06 },
    imageUrl:
      "https://images.unsplash.com/photo-1538485399081-7191377e8241?w=800&q=80",
  },
  {
    id: "KR-PUS-GMC",
    cityId: "KR-PUS",
    name: {
      en: "Gamcheon Culture Village",
      ko: "감천문화마을",
      ja: "甘川文化村",
      "zh-CN": "甘川文化村",
      "zh-TW": "甘川文化村",
    },
    coordinates: { latitude: 35.0972, longitude: 129.0105 },
    imageUrl:
      "https://images.unsplash.com/photo-1538485399081-7191377e8241?w=800&q=80",
  },

  // ===== 제주 (Jeju) =====
  {
    id: "KR-JEJ-JJC",
    cityId: "KR-JEJ",
    name: {
      en: "Jeju City",
      ko: "제주시",
      ja: "済州市",
      "zh-CN": "济州市",
      "zh-TW": "濟州市",
    },
    coordinates: { latitude: 33.4996, longitude: 126.5312 },
    imageUrl:
      "https://images.unsplash.com/photo-1597655601841-214a4cfe8b2c?w=800&q=80",
  },
  {
    id: "KR-JEJ-SGP",
    cityId: "KR-JEJ",
    name: {
      en: "Seogwipo",
      ko: "서귀포",
      ja: "西帰浦",
      "zh-CN": "西归浦",
      "zh-TW": "西歸浦",
    },
    coordinates: { latitude: 33.253, longitude: 126.56 },
    imageUrl:
      "https://images.unsplash.com/photo-1570077188670-e3a8d69ac5ff?w=800&q=80",
  },
  {
    id: "KR-JEJ-HSN",
    cityId: "KR-JEJ",
    name: {
      en: "Hallasan",
      ko: "한라산",
      ja: "漢拏山",
      "zh-CN": "汉拿山",
      "zh-TW": "漢拏山",
    },
    coordinates: { latitude: 33.3617, longitude: 126.5292 },
    imageUrl:
      "https://images.unsplash.com/photo-1544376798-89aa6b82c6cd?w=800&q=80",
  },
  {
    id: "KR-JEJ-USD",
    cityId: "KR-JEJ",
    name: {
      en: "Udo Island",
      ko: "우도",
      ja: "牛島",
      "zh-CN": "牛岛",
      "zh-TW": "牛島",
    },
    coordinates: { latitude: 33.5, longitude: 126.95 },
    imageUrl:
      "https://images.unsplash.com/photo-1570077188670-e3a8d69ac5ff?w=800&q=80",
  },

  // ===== 도쿄 (Tokyo) =====
  {
    id: "JP-TYO-SBY",
    cityId: "JP-TYO",
    name: {
      en: "Shibuya",
      ko: "시부야",
      ja: "渋谷",
      "zh-CN": "�的谷",
      "zh-TW": "澀谷",
    },
    coordinates: { latitude: 35.658, longitude: 139.7016 },
    imageUrl:
      "https://images.unsplash.com/photo-1542051841857-5f90071e7989?w=800&q=80",
  },
  {
    id: "JP-TYO-SJK",
    cityId: "JP-TYO",
    name: {
      en: "Shinjuku",
      ko: "신주쿠",
      ja: "新宿",
      "zh-CN": "新宿",
      "zh-TW": "新宿",
    },
    coordinates: { latitude: 35.6938, longitude: 139.7034 },
    imageUrl:
      "https://images.unsplash.com/photo-1536098561742-ca998e48cbcc?w=800&q=80",
  },
  {
    id: "JP-TYO-HRJ",
    cityId: "JP-TYO",
    name: {
      en: "Harajuku/Omotesando",
      ko: "하라주쿠/오모테산도",
      ja: "原宿/表参道",
      "zh-CN": "原宿/表参道",
      "zh-TW": "原宿/表參道",
    },
    coordinates: { latitude: 35.6702, longitude: 139.7027 },
    imageUrl:
      "https://images.unsplash.com/photo-1528360983277-13d401cdc186?w=800&q=80",
  },
  {
    id: "JP-TYO-GNZ",
    cityId: "JP-TYO",
    name: {
      en: "Ginza",
      ko: "긴자",
      ja: "銀座",
      "zh-CN": "银座",
      "zh-TW": "銀座",
    },
    coordinates: { latitude: 35.6721, longitude: 139.7636 },
    imageUrl:
      "https://images.unsplash.com/photo-1554797589-7241bb691973?w=800&q=80",
  },
  {
    id: "JP-TYO-ASK",
    cityId: "JP-TYO",
    name: {
      en: "Asakusa",
      ko: "아사쿠사",
      ja: "浅草",
      "zh-CN": "浅草",
      "zh-TW": "淺草",
    },
    coordinates: { latitude: 35.7147, longitude: 139.7966 },
    imageUrl:
      "https://images.unsplash.com/photo-1545569341-9eb8b30979d9?w=800&q=80",
  },
  {
    id: "JP-TYO-AKH",
    cityId: "JP-TYO",
    name: {
      en: "Akihabara",
      ko: "아키하바라",
      ja: "秋葉原",
      "zh-CN": "秋叶原",
      "zh-TW": "秋葉原",
    },
    coordinates: { latitude: 35.7022, longitude: 139.7741 },
    imageUrl:
      "https://images.unsplash.com/photo-1480796927426-f609979314bd?w=800&q=80",
  },
  {
    id: "JP-TYO-ODB",
    cityId: "JP-TYO",
    name: {
      en: "Odaiba",
      ko: "오다이바",
      ja: "お台場",
      "zh-CN": "台场",
      "zh-TW": "台場",
    },
    coordinates: { latitude: 35.6268, longitude: 139.7766 },
    imageUrl:
      "https://images.unsplash.com/photo-1540959733332-eab4deabeeaf?w=800&q=80",
  },
  {
    id: "JP-TYO-UEN",
    cityId: "JP-TYO",
    name: {
      en: "Ueno",
      ko: "우에노",
      ja: "上野",
      "zh-CN": "上野",
      "zh-TW": "上野",
    },
    coordinates: { latitude: 35.7141, longitude: 139.7774 },
    imageUrl:
      "https://images.unsplash.com/photo-1524413840807-0c3cb6fa808d?w=800&q=80",
  },

  // ===== 오사카 (Osaka) =====
  {
    id: "JP-OSA-DOT",
    cityId: "JP-OSA",
    name: {
      en: "Dotonbori",
      ko: "도톤보리",
      ja: "道頓堀",
      "zh-CN": "道顿堀",
      "zh-TW": "道頓堀",
    },
    coordinates: { latitude: 34.6687, longitude: 135.503 },
    imageUrl:
      "https://images.unsplash.com/photo-1590559899731-a382839e5549?w=800&q=80",
  },
  {
    id: "JP-OSA-UMD",
    cityId: "JP-OSA",
    name: {
      en: "Umeda",
      ko: "우메다",
      ja: "梅田",
      "zh-CN": "梅田",
      "zh-TW": "梅田",
    },
    coordinates: { latitude: 34.7024, longitude: 135.4959 },
    imageUrl:
      "https://images.unsplash.com/photo-1590559899731-a382839e5549?w=800&q=80",
  },
  {
    id: "JP-OSA-NMB",
    cityId: "JP-OSA",
    name: {
      en: "Namba",
      ko: "난바",
      ja: "難波",
      "zh-CN": "难波",
      "zh-TW": "難波",
    },
    coordinates: { latitude: 34.666, longitude: 135.5014 },
    imageUrl:
      "https://images.unsplash.com/photo-1493976040374-85c8e12f0c0e?w=800&q=80",
  },
  {
    id: "JP-OSA-SHN",
    cityId: "JP-OSA",
    name: {
      en: "Shinsaibashi",
      ko: "신사이바시",
      ja: "心斎橋",
      "zh-CN": "心斋桥",
      "zh-TW": "心齋橋",
    },
    coordinates: { latitude: 34.6751, longitude: 135.5008 },
    imageUrl:
      "https://images.unsplash.com/photo-1524413840807-0c3cb6fa808d?w=800&q=80",
  },
  {
    id: "JP-OSA-OSC",
    cityId: "JP-OSA",
    name: {
      en: "Osaka Castle",
      ko: "오사카성",
      ja: "大阪城",
      "zh-CN": "大阪城",
      "zh-TW": "大阪城",
    },
    coordinates: { latitude: 34.6873, longitude: 135.5259 },
    imageUrl:
      "https://images.unsplash.com/photo-1589452271712-64b8a66c7b71?w=800&q=80",
  },
  {
    id: "JP-OSA-USJ",
    cityId: "JP-OSA",
    name: {
      en: "Universal Studios Japan",
      ko: "유니버설 스튜디오",
      ja: "ユニバーサル・スタジオ",
      "zh-CN": "环球影城",
      "zh-TW": "環球影城",
    },
    coordinates: { latitude: 34.6654, longitude: 135.4323 },
    imageUrl:
      "https://images.unsplash.com/photo-1565402170291-8491f14678db?w=800&q=80",
  },

  // ===== 방콕 (Bangkok) =====
  {
    id: "TH-BKK-SKV",
    cityId: "TH-BKK",
    name: {
      en: "Sukhumvit",
      ko: "수쿰빗",
      ja: "スクンビット",
      "zh-CN": "素坤逸",
      "zh-TW": "素坤逸",
    },
    coordinates: { latitude: 13.7372, longitude: 100.5607 },
    imageUrl:
      "https://images.unsplash.com/photo-1563492065599-3520f775eeed?w=800&q=80",
  },
  {
    id: "TH-BKK-SLM",
    cityId: "TH-BKK",
    name: {
      en: "Silom",
      ko: "실롬",
      ja: "シーロム",
      "zh-CN": "是隆",
      "zh-TW": "是隆",
    },
    coordinates: { latitude: 13.7263, longitude: 100.5294 },
    imageUrl:
      "https://images.unsplash.com/photo-1519451241324-20b4ea2c4220?w=800&q=80",
  },
  {
    id: "TH-BKK-KSR",
    cityId: "TH-BKK",
    name: {
      en: "Khao San Road",
      ko: "카오산로드",
      ja: "カオサン通り",
      "zh-CN": "考山路",
      "zh-TW": "考山路",
    },
    coordinates: { latitude: 13.7583, longitude: 100.4971 },
    imageUrl:
      "https://images.unsplash.com/photo-1559592413-7cec4d0cae2b?w=800&q=80",
  },
  {
    id: "TH-BKK-SIM",
    cityId: "TH-BKK",
    name: {
      en: "Siam",
      ko: "시암",
      ja: "サイアム",
      "zh-CN": "暹罗",
      "zh-TW": "暹羅",
    },
    coordinates: { latitude: 13.7462, longitude: 100.5341 },
    imageUrl:
      "https://images.unsplash.com/photo-1508009603885-50cf7c579365?w=800&q=80",
  },
  {
    id: "TH-BKK-RTN",
    cityId: "TH-BKK",
    name: {
      en: "Rattanakosin (Old City)",
      ko: "랏따나꼬신",
      ja: "ラタナコーシン",
      "zh-CN": "拉达那哥欣",
      "zh-TW": "拉達那哥欣",
    },
    coordinates: { latitude: 13.752, longitude: 100.4927 },
    imageUrl:
      "https://images.unsplash.com/photo-1528181304800-259b08848526?w=800&q=80",
  },
  {
    id: "TH-BKK-CHT",
    cityId: "TH-BKK",
    name: {
      en: "Chatuchak",
      ko: "짜뚜짝",
      ja: "チャトゥチャック",
      "zh-CN": "乍都乍",
      "zh-TW": "乍都乍",
    },
    coordinates: { latitude: 13.7999, longitude: 100.5504 },
    imageUrl:
      "https://images.unsplash.com/photo-1508009603885-50cf7c579365?w=800&q=80",
  },

  // ===== 싱가포르 (Singapore) =====
  {
    id: "SG-SIN-MBS",
    cityId: "SG-SIN",
    name: {
      en: "Marina Bay",
      ko: "마리나베이",
      ja: "マリーナベイ",
      "zh-CN": "滨海湾",
      "zh-TW": "濱海灣",
    },
    coordinates: { latitude: 1.2834, longitude: 103.8607 },
    imageUrl:
      "https://images.unsplash.com/photo-1525625293386-3f8f99389edd?w=800&q=80",
  },
  {
    id: "SG-SIN-ORC",
    cityId: "SG-SIN",
    name: {
      en: "Orchard Road",
      ko: "오차드로드",
      ja: "オーチャードロード",
      "zh-CN": "乌节路",
      "zh-TW": "烏節路",
    },
    coordinates: { latitude: 1.3048, longitude: 103.8318 },
    imageUrl:
      "https://images.unsplash.com/photo-1565967511849-76a60a516170?w=800&q=80",
  },
  {
    id: "SG-SIN-SNT",
    cityId: "SG-SIN",
    name: {
      en: "Sentosa",
      ko: "센토사",
      ja: "セントーサ",
      "zh-CN": "圣淘沙",
      "zh-TW": "聖淘沙",
    },
    coordinates: { latitude: 1.2494, longitude: 103.8303 },
    imageUrl:
      "https://images.unsplash.com/photo-1532375810709-75b1da00537c?w=800&q=80",
  },
  {
    id: "SG-SIN-CHT",
    cityId: "SG-SIN",
    name: {
      en: "Chinatown",
      ko: "차이나타운",
      ja: "チャイナタウン",
      "zh-CN": "牛车水",
      "zh-TW": "牛車水",
    },
    coordinates: { latitude: 1.2832, longitude: 103.8446 },
    imageUrl:
      "https://images.unsplash.com/photo-1508964942454-1a56651d54ac?w=800&q=80",
  },
  {
    id: "SG-SIN-LIT",
    cityId: "SG-SIN",
    name: {
      en: "Little India",
      ko: "리틀인디아",
      ja: "リトルインディア",
      "zh-CN": "小印度",
      "zh-TW": "小印度",
    },
    coordinates: { latitude: 1.3066, longitude: 103.8518 },
    imageUrl:
      "https://images.unsplash.com/photo-1565967511849-76a60a516170?w=800&q=80",
  },

  // ===== 교토 (Kyoto) =====
  {
    id: "JP-KYO-GIO",
    cityId: "JP-KYO",
    name: {
      en: "Gion",
      ko: "기온",
      ja: "祇園",
      "zh-CN": "�的园",
      "zh-TW": "祇園",
    },
    coordinates: { latitude: 35.0037, longitude: 135.7756 },
    imageUrl:
      "https://images.unsplash.com/photo-1493976040374-85c8e12f0c0e?w=800&q=80",
  },
  {
    id: "JP-KYO-ARS",
    cityId: "JP-KYO",
    name: {
      en: "Arashiyama",
      ko: "아라시야마",
      ja: "嵐山",
      "zh-CN": "岚山",
      "zh-TW": "嵐山",
    },
    coordinates: { latitude: 35.0094, longitude: 135.6719 },
    imageUrl:
      "https://images.unsplash.com/photo-1478436127897-769e1b3f0f36?w=800&q=80",
  },
  {
    id: "JP-KYO-FSI",
    cityId: "JP-KYO",
    name: {
      en: "Fushimi Inari",
      ko: "후시미이나리",
      ja: "伏見稲荷",
      "zh-CN": "伏见稻荷",
      "zh-TW": "伏見稻荷",
    },
    coordinates: { latitude: 34.9671, longitude: 135.7727 },
    imageUrl:
      "https://images.unsplash.com/photo-1528360983277-13d401cdc186?w=800&q=80",
  },
  {
    id: "JP-KYO-KNK",
    cityId: "JP-KYO",
    name: {
      en: "Kinkaku-ji (Golden Pavilion)",
      ko: "킨카쿠지",
      ja: "金閣寺",
      "zh-CN": "金阁寺",
      "zh-TW": "金閣寺",
    },
    coordinates: { latitude: 35.0394, longitude: 135.7292 },
    imageUrl:
      "https://images.unsplash.com/photo-1502920514313-52581002a659?w=800&q=80",
  },

  // ===== 베트남 - 다낭 (Da Nang) =====
  {
    id: "VN-DAD-MKB",
    cityId: "VN-DAD",
    name: {
      en: "My Khe Beach",
      ko: "미케비치",
      ja: "ミーケービーチ",
      "zh-CN": "美溪海滩",
      "zh-TW": "美溪海灘",
    },
    coordinates: { latitude: 16.0544, longitude: 108.2478 },
    imageUrl:
      "https://images.unsplash.com/photo-1559592413-7cec4d0cae2b?w=800&q=80",
  },
  {
    id: "VN-DAD-HAN",
    cityId: "VN-DAD",
    name: {
      en: "Han River",
      ko: "한강",
      ja: "ハン川",
      "zh-CN": "韩江",
      "zh-TW": "韓江",
    },
    coordinates: { latitude: 16.0678, longitude: 108.2241 },
    imageUrl:
      "https://images.unsplash.com/photo-1583417319070-4a69db38a482?w=800&q=80",
  },
  {
    id: "VN-DAD-BAH",
    cityId: "VN-DAD",
    name: {
      en: "Ba Na Hills",
      ko: "바나힐",
      ja: "バナヒルズ",
      "zh-CN": "巴拿山",
      "zh-TW": "巴拿山",
    },
    coordinates: { latitude: 15.9977, longitude: 107.9875 },
    imageUrl:
      "https://images.unsplash.com/photo-1570077188670-e3a8d69ac5ff?w=800&q=80",
  },

  // ===== 베트남 - 호이안 (Hoi An) =====
  {
    id: "VN-HOI-OLD",
    cityId: "VN-HOI",
    name: {
      en: "Hoi An Ancient Town",
      ko: "호이안 구시가지",
      ja: "ホイアン旧市街",
      "zh-CN": "会安古镇",
      "zh-TW": "會安古鎮",
    },
    coordinates: { latitude: 15.8801, longitude: 108.338 },
    imageUrl:
      "https://images.unsplash.com/photo-1559592413-7cec4d0cae2b?w=800&q=80",
  },
  {
    id: "VN-HOI-ANB",
    cityId: "VN-HOI",
    name: {
      en: "An Bang Beach",
      ko: "안방비치",
      ja: "アンバンビーチ",
      "zh-CN": "安邦海滩",
      "zh-TW": "安邦海灘",
    },
    coordinates: { latitude: 15.905, longitude: 108.369 },
    imageUrl:
      "https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=800&q=80",
  },

  // ===== 베트남 - 나트랑 (Nha Trang) =====
  {
    id: "VN-NHA-BCH",
    cityId: "VN-NHA",
    name: {
      en: "Nha Trang Beach",
      ko: "나트랑 비치",
      ja: "ニャチャンビーチ",
      "zh-CN": "芽庄海滩",
      "zh-TW": "芽莊海灘",
    },
    coordinates: { latitude: 12.2451, longitude: 109.1943 },
    imageUrl:
      "https://images.unsplash.com/photo-1573843981267-be1999ff37cd?w=800&q=80",
  },
  {
    id: "VN-NHA-VPL",
    cityId: "VN-NHA",
    name: {
      en: "VinWonders",
      ko: "빈원더스",
      ja: "ビンワンダーズ",
      "zh-CN": "珍珠岛乐园",
      "zh-TW": "珍珠島樂園",
    },
    coordinates: { latitude: 12.2167, longitude: 109.25 },
    imageUrl:
      "https://images.unsplash.com/photo-1559592413-7cec4d0cae2b?w=800&q=80",
  },

  // ===== 홍콩 (Hong Kong) =====
  {
    id: "HK-HKG-TST",
    cityId: "HK-HKG",
    name: {
      en: "Tsim Sha Tsui",
      ko: "침사추이",
      ja: "尖沙咀",
      "zh-CN": "尖沙咀",
      "zh-TW": "尖沙咀",
    },
    coordinates: { latitude: 22.2988, longitude: 114.1722 },
    imageUrl:
      "https://images.unsplash.com/photo-1536599018102-9f803c140fc1?w=800&q=80",
  },
  {
    id: "HK-HKG-CWB",
    cityId: "HK-HKG",
    name: {
      en: "Causeway Bay",
      ko: "코즈웨이베이",
      ja: "銅鑼灣",
      "zh-CN": "铜锣湾",
      "zh-TW": "銅鑼灣",
    },
    coordinates: { latitude: 22.2802, longitude: 114.184 },
    imageUrl:
      "https://images.unsplash.com/photo-1536599018102-9f803c140fc1?w=800&q=80",
  },
  {
    id: "HK-HKG-MKT",
    cityId: "HK-HKG",
    name: {
      en: "Mong Kok",
      ko: "몽콕",
      ja: "旺角",
      "zh-CN": "旺角",
      "zh-TW": "旺角",
    },
    coordinates: { latitude: 22.319, longitude: 114.1694 },
    imageUrl:
      "https://images.unsplash.com/photo-1518005020951-eccb494ad742?w=800&q=80",
  },
  {
    id: "HK-HKG-CEN",
    cityId: "HK-HKG",
    name: {
      en: "Central",
      ko: "센트럴",
      ja: "中環",
      "zh-CN": "中环",
      "zh-TW": "中環",
    },
    coordinates: { latitude: 22.2819, longitude: 114.158 },
    imageUrl:
      "https://images.unsplash.com/photo-1513326738677-b964603b136d?w=800&q=80",
  },
  {
    id: "HK-HKG-LKF",
    cityId: "HK-HKG",
    name: {
      en: "Lan Kwai Fong",
      ko: "란콰이펑",
      ja: "蘭桂坊",
      "zh-CN": "兰桂坊",
      "zh-TW": "蘭桂坊",
    },
    coordinates: { latitude: 22.2809, longitude: 114.1555 },
    imageUrl:
      "https://images.unsplash.com/photo-1513326738677-b964603b136d?w=800&q=80",
  },

  // ===== 대만 - 타이베이 (Taipei) =====
  {
    id: "TW-TPE-XMD",
    cityId: "TW-TPE",
    name: {
      en: "Ximending",
      ko: "시먼딩",
      ja: "西門町",
      "zh-CN": "西门町",
      "zh-TW": "西門町",
    },
    coordinates: { latitude: 25.0421, longitude: 121.5081 },
    imageUrl:
      "https://images.unsplash.com/photo-1470004914212-05527e49370b?w=800&q=80",
  },
  {
    id: "TW-TPE-T101",
    cityId: "TW-TPE",
    name: {
      en: "Taipei 101/Xinyi",
      ko: "타이베이101/신이",
      ja: "台北101/信義",
      "zh-CN": "台北101/信义",
      "zh-TW": "台北101/信義",
    },
    coordinates: { latitude: 25.0339, longitude: 121.5645 },
    imageUrl:
      "https://images.unsplash.com/photo-1508009603885-50cf7c579365?w=800&q=80",
  },
  {
    id: "TW-TPE-SHL",
    cityId: "TW-TPE",
    name: {
      en: "Shilin Night Market",
      ko: "스린야시장",
      ja: "士林夜市",
      "zh-CN": "士林夜市",
      "zh-TW": "士林夜市",
    },
    coordinates: { latitude: 25.0878, longitude: 121.524 },
    imageUrl:
      "https://images.unsplash.com/photo-1555939594-58d7cb561ad1?w=800&q=80",
  },
  {
    id: "TW-TPE-JFS",
    cityId: "TW-TPE",
    name: {
      en: "Jiufen",
      ko: "지우펀",
      ja: "九份",
      "zh-CN": "九份",
      "zh-TW": "九份",
    },
    coordinates: { latitude: 25.1089, longitude: 121.8449 },
    imageUrl:
      "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800&q=80",
  },
  {
    id: "TW-TPE-BET",
    cityId: "TW-TPE",
    name: {
      en: "Beitou Hot Springs",
      ko: "베이터우 온천",
      ja: "北投温泉",
      "zh-CN": "北投温泉",
      "zh-TW": "北投溫泉",
    },
    coordinates: { latitude: 25.1366, longitude: 121.5044 },
    imageUrl:
      "https://images.unsplash.com/photo-1540555700478-4be289fbecef?w=800&q=80",
  },

  // ===== 인도네시아 - 발리 (Bali) =====
  {
    id: "ID-DPS-KUT",
    cityId: "ID-DPS",
    name: {
      en: "Kuta",
      ko: "꾸따",
      ja: "クタ",
      "zh-CN": "库塔",
      "zh-TW": "庫塔",
    },
    coordinates: { latitude: -8.718, longitude: 115.1686 },
    imageUrl:
      "https://images.unsplash.com/photo-1537996194471-e657df975ab4?w=800&q=80",
  },
  {
    id: "ID-DPS-SMY",
    cityId: "ID-DPS",
    name: {
      en: "Seminyak",
      ko: "스미냑",
      ja: "スミニャック",
      "zh-CN": "水明漾",
      "zh-TW": "水明漾",
    },
    coordinates: { latitude: -8.6912, longitude: 115.1573 },
    imageUrl:
      "https://images.unsplash.com/photo-1559628233-100c798642d4?w=800&q=80",
  },
  {
    id: "ID-DPS-UBD",
    cityId: "ID-DPS",
    name: {
      en: "Ubud",
      ko: "우붓",
      ja: "ウブド",
      "zh-CN": "乌布",
      "zh-TW": "烏布",
    },
    coordinates: { latitude: -8.5069, longitude: 115.2625 },
    imageUrl:
      "https://images.unsplash.com/photo-1555400038-63f5ba517a47?w=800&q=80",
  },
  {
    id: "ID-DPS-NSD",
    cityId: "ID-DPS",
    name: {
      en: "Nusa Dua",
      ko: "누사두아",
      ja: "ヌサドゥア",
      "zh-CN": "努沙杜瓦",
      "zh-TW": "努沙杜瓦",
    },
    coordinates: { latitude: -8.8007, longitude: 115.2318 },
    imageUrl:
      "https://images.unsplash.com/photo-1577717903315-1691ae25ab3f?w=800&q=80",
  },
  {
    id: "ID-DPS-CGU",
    cityId: "ID-DPS",
    name: {
      en: "Canggu",
      ko: "짱구",
      ja: "チャングー",
      "zh-CN": "仓古",
      "zh-TW": "倉古",
    },
    coordinates: { latitude: -8.6478, longitude: 115.1385 },
    imageUrl:
      "https://images.unsplash.com/photo-1558005530-a7958896ec60?w=800&q=80",
  },

  // ===== 태국 - 푸켓 (Phuket) =====
  {
    id: "TH-HKT-PTG",
    cityId: "TH-HKT",
    name: {
      en: "Patong Beach",
      ko: "빠통비치",
      ja: "パトンビーチ",
      "zh-CN": "芭东海滩",
      "zh-TW": "芭東海灘",
    },
    coordinates: { latitude: 7.8965, longitude: 98.2962 },
    imageUrl:
      "https://images.unsplash.com/photo-1589394815804-964ed0be2eb5?w=800&q=80",
  },
  {
    id: "TH-HKT-KTA",
    cityId: "TH-HKT",
    name: {
      en: "Kata Beach",
      ko: "까따비치",
      ja: "カタビーチ",
      "zh-CN": "卡塔海滩",
      "zh-TW": "卡塔海灘",
    },
    coordinates: { latitude: 7.8205, longitude: 98.2981 },
    imageUrl:
      "https://images.unsplash.com/photo-1506665531195-3566af2b4dfa?w=800&q=80",
  },
  {
    id: "TH-HKT-OLD",
    cityId: "TH-HKT",
    name: {
      en: "Phuket Old Town",
      ko: "푸켓 올드타운",
      ja: "プーケットオールドタウン",
      "zh-CN": "普吉老城",
      "zh-TW": "普吉老城",
    },
    coordinates: { latitude: 7.8842, longitude: 98.3886 },
    imageUrl:
      "https://images.unsplash.com/photo-1552465011-b4e21bf6e79a?w=800&q=80",
  },

  // ===== 말레이시아 - 쿠알라룸푸르 (Kuala Lumpur) =====
  {
    id: "MY-KUL-BKT",
    cityId: "MY-KUL",
    name: {
      en: "Bukit Bintang",
      ko: "부킷빈탕",
      ja: "ブキッビンタン",
      "zh-CN": "武吉免登",
      "zh-TW": "武吉免登",
    },
    coordinates: { latitude: 3.1466, longitude: 101.7105 },
    imageUrl:
      "https://images.unsplash.com/photo-1596422846543-75c6fc197f07?w=800&q=80",
  },
  {
    id: "MY-KUL-PTT",
    cityId: "MY-KUL",
    name: {
      en: "Petronas Towers/KLCC",
      ko: "페트로나스 타워/KLCC",
      ja: "ペトロナスタワー/KLCC",
      "zh-CN": "双子塔/KLCC",
      "zh-TW": "雙子塔/KLCC",
    },
    coordinates: { latitude: 3.1579, longitude: 101.7117 },
    imageUrl:
      "https://images.unsplash.com/photo-1508062878650-88b52897f298?w=800&q=80",
  },
  {
    id: "MY-KUL-CHT",
    cityId: "MY-KUL",
    name: {
      en: "Chinatown (Petaling Street)",
      ko: "차이나타운",
      ja: "チャイナタウン",
      "zh-CN": "茨厂街",
      "zh-TW": "茨廠街",
    },
    coordinates: { latitude: 3.1428, longitude: 101.6966 },
    imageUrl:
      "https://images.unsplash.com/photo-1595435934249-5df7ed86e1c0?w=800&q=80",
  },
  {
    id: "MY-KUL-BCT",
    cityId: "MY-KUL",
    name: {
      en: "Batu Caves",
      ko: "바투 동굴",
      ja: "バトゥ洞窟",
      "zh-CN": "黑风洞",
      "zh-TW": "黑風洞",
    },
    coordinates: { latitude: 3.2378, longitude: 101.684 },
    imageUrl:
      "https://images.unsplash.com/photo-1596422846543-75c6fc197f07?w=800&q=80",
  },

  // ===== 필리핀 - 세부 (Cebu) =====
  {
    id: "PH-CEB-MCT",
    cityId: "PH-CEB",
    name: {
      en: "Mactan Island",
      ko: "막탄섬",
      ja: "マクタン島",
      "zh-CN": "麦克坦岛",
      "zh-TW": "麥克坦島",
    },
    coordinates: { latitude: 10.3103, longitude: 123.9799 },
    imageUrl:
      "https://images.unsplash.com/photo-1573843981267-be1999ff37cd?w=800&q=80",
  },
  {
    id: "PH-CEB-OSL",
    cityId: "PH-CEB",
    name: {
      en: "Oslob (Whale Sharks)",
      ko: "오슬롭",
      ja: "オスロブ",
      "zh-CN": "奥斯洛布",
      "zh-TW": "奧斯洛布",
    },
    coordinates: { latitude: 9.4627, longitude: 123.3889 },
    imageUrl:
      "https://images.unsplash.com/photo-1544551763-46a013bb70d5?w=800&q=80",
  },
  {
    id: "PH-CEB-DWN",
    cityId: "PH-CEB",
    name: {
      en: "Cebu Downtown",
      ko: "세부 다운타운",
      ja: "セブダウンタウン",
      "zh-CN": "宿务市中心",
      "zh-TW": "宿霧市中心",
    },
    coordinates: { latitude: 10.3157, longitude: 123.8854 },
    imageUrl:
      "https://images.unsplash.com/photo-1518509562904-e7ef99cdcc86?w=800&q=80",
  },
];

/**
 * locale별 핫스팟 순서 (추천 순)
 */
export const HOT_SPOT_ORDER: Record<string, string[]> = {
  // 한국 사용자용 - 한국 주요 지역 + 해외 인기 여행지
  ko: [
    // 서울
    "KR-SEL-HDS",
    "KR-SEL-GNS",
    "KR-SEL-HNI",
    "KR-SEL-JGU",
    // 일본
    "JP-TYO-SBY",
    "JP-TYO-SJK",
    "JP-OSA-DOT",
    // 동남아
    "VN-DAD-MKB",
    "TH-BKK-SKV",
    "ID-DPS-UBD",
    // 부산/제주
    "KR-PUS-HUD",
    "KR-JEJ-JJC",
    // 기타 해외
    "TW-TPE-XMD",
    "HK-HKG-TST",
    "SG-SIN-MBS",
    "TH-HKT-PTG",
    "MY-KUL-BKT",
    "PH-CEB-MCT",
    "VN-HOI-OLD",
    "VN-NHA-BCH",
  ],
  // 일본 사용자용 - 일본 주요 지역 + 해외 인기 여행지
  ja: [
    // 도쿄
    "JP-TYO-SBY",
    "JP-TYO-SJK",
    "JP-TYO-HRJ",
    "JP-TYO-ASK",
    // 오사카/교토
    "JP-OSA-DOT",
    "JP-OSA-SHN",
    "JP-KYO-GIO",
    "JP-KYO-ARS",
    // 한국
    "KR-SEL-HDS",
    "KR-SEL-GNS",
    // 동남아
    "TH-BKK-SKV",
    "SG-SIN-MBS",
    "TW-TPE-XMD",
    "ID-DPS-UBD",
    "VN-DAD-MKB",
    "HK-HKG-TST",
  ],
  // 영어권 사용자용 - 아시아 전역 인기 여행지
  en: [
    // 일본
    "JP-TYO-SBY",
    "JP-TYO-ASK",
    "JP-OSA-DOT",
    "JP-KYO-GIO",
    // 한국
    "KR-SEL-HDS",
    "KR-SEL-GNS",
    // 태국
    "TH-BKK-SKV",
    "TH-HKT-PTG",
    // 발리
    "ID-DPS-UBD",
    "ID-DPS-SMY",
    // 베트남
    "VN-DAD-MKB",
    "VN-HOI-OLD",
    // 기타
    "SG-SIN-MBS",
    "HK-HKG-TST",
    "TW-TPE-XMD",
    "MY-KUL-PTT",
  ],
  // 중국어 간체 사용자용
  "zh-CN": [
    // 일본
    "JP-TYO-SBY",
    "JP-TYO-GNZ",
    "JP-OSA-DOT",
    "JP-OSA-USJ",
    // 한국
    "KR-SEL-HDS",
    "KR-SEL-JGU",
    // 태국
    "TH-BKK-SIM",
    "TH-HKT-PTG",
    // 홍콩/대만
    "HK-HKG-TST",
    "HK-HKG-CWB",
    "TW-TPE-XMD",
    "TW-TPE-JFS",
    // 기타
    "SG-SIN-MBS",
    "ID-DPS-UBD",
    "VN-DAD-MKB",
    "MY-KUL-PTT",
  ],
  // 중국어 번체 사용자용 (대만)
  "zh-TW": [
    // 대만
    "TW-TPE-XMD",
    "TW-TPE-T101",
    "TW-TPE-SHL",
    "TW-TPE-JFS",
    // 일본
    "JP-TYO-SBY",
    "JP-OSA-DOT",
    "JP-KYO-GIO",
    // 한국
    "KR-SEL-HDS",
    "KR-SEL-GNS",
    // 홍콩
    "HK-HKG-TST",
    "HK-HKG-CWB",
    // 동남아
    "TH-BKK-SKV",
    "SG-SIN-MBS",
    "ID-DPS-UBD",
    "VN-DAD-MKB",
  ],
};

/**
 * locale에 맞는 핫스팟 목록 가져오기
 * @param locale 언어 코드
 * @param limit 최대 개수 (기본 12)
 * @returns 핫스팟 배열
 */
export function getHotSpots(locale: string, limit: number = 12): HotSpot[] {
  const spotIds = HOT_SPOT_ORDER[locale] || HOT_SPOT_ORDER["en"];

  return spotIds
    .slice(0, limit)
    .map((id) => HOT_SPOTS.find((spot) => spot.id === id))
    .filter((spot): spot is HotSpot => spot !== undefined);
}

/**
 * 특정 도시의 핫스팟 목록 가져오기
 * @param cityId 도시 ID
 * @returns 해당 도시의 핫스팟 배열
 */
export function getHotSpotsByCity(cityId: string): HotSpot[] {
  return HOT_SPOTS.filter((spot) => spot.cityId === cityId);
}

/**
 * 핫스팟 ID로 핫스팟 찾기
 * @param spotId 핫스팟 ID
 * @returns 핫스팟 (없으면 undefined)
 */
export function getHotSpotById(spotId: string): HotSpot | undefined {
  return HOT_SPOTS.find((spot) => spot.id === spotId);
}

/**
 * 도시명으로 도시 ID 찾기 (다국어 지원)
 * @param cityName 도시명 (한국어, 영어, 일본어 등)
 * @returns 도시 ID (없으면 undefined)
 */
export function getCityIdByName(cityName: string): string | undefined {
  const normalizedName = cityName.toLowerCase().trim();

  const city = CITIES.find(
    (c) =>
      c.name.ko?.toLowerCase() === normalizedName ||
      c.name.en?.toLowerCase() === normalizedName ||
      c.name.ja?.toLowerCase() === normalizedName ||
      c.name["zh-CN"]?.toLowerCase() === normalizedName ||
      c.name["zh-TW"]?.toLowerCase() === normalizedName,
  );

  return city?.id;
}

/**
 * 우선순위 도시의 핫스팟을 앞에 배치한 핫스팟 목록 가져오기
 * @param locale 로케일
 * @param priorityCityId 우선순위로 표시할 도시 ID
 * @param limit 최대 개수
 * @returns 우선순위 도시 핫스팟 + 나머지 핫스팟
 */
export function getHotSpotsWithPriority(
  locale: string,
  priorityCityId?: string,
  limit: number = 15,
): HotSpot[] {
  if (!priorityCityId) {
    return getHotSpots(locale, limit);
  }

  // 우선순위 도시의 핫스팟
  const prioritySpots = getHotSpotsByCity(priorityCityId);

  // 나머지 핫스팟 (우선순위 도시 제외)
  const otherSpots = getHotSpots(locale, limit * 2).filter(
    (spot) => spot.cityId !== priorityCityId,
  );

  // 우선순위 핫스팟 + 나머지 핫스팟 (limit 내)
  return [...prioritySpots, ...otherSpots].slice(0, limit);
}
